import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    getMapData, getArpData, getCnsData, getThrData, getRwyData, getRwyProfileData, getAirportsData,
    createArp, createCns, createThr, createRwy, createRwyProfile, createAirport,
    updateArp, updateCns, updateThr, updateRwy, updateRwyProfile, updateAirport,
    deleteArp, deleteCns, deleteThr, deleteRwy, deleteRwyProfile, deleteAirport,
    getGeoBorders, toggleGeoBorder,
    getIlsProcs, createIlsProc, updateIlsProc, deleteIlsProc,
    getOasConstants, createOasConstant, updateOasConstant, deleteOasConstant,
    getOasTemplates, createOasTemplate, updateOasTemplate, deleteOasTemplate,
    getWaypoints, createWaypoint, updateWaypoint, deleteWaypoint,
    getAirspaceData, createAirspace, updateAirspace, deleteAirspace,
    uploadAirspaceKmz, bulkUploadAirspace, reparseAirspace,
    getProcedures, deleteProcedure, bulkUploadProcs, uploadProcedure, reparseAllProcs, patchTaaAltitudes, getProcLayers,
    getRmacList, bulkUploadRmac, uploadRmac, deleteRmac, bulkDeleteRmac, getAllRmacMinima, reparseAllRmac,
    getCirclingMinimaData, createCirclingMinima, updateCirclingMinima, deleteCirclingMinima,
    getMsaMinimaData, createMsaMinima, updateMsaMinima, deleteMsaMinima,
    getMsaSectors, bulkUploadMsaSectors, deleteMsaSectors,
    getRnavRoutes, uploadRnavRoute, bulkUploadRnavRoutes, reparseRnavRoutes, clearAllRnavRoutes, deleteRnavRoute,
    getDepartures, deleteDeparture, bulkUploadDepartures, uploadDeparture, reparseAllDepartures,
    getArrivals, deleteArrival, bulkUploadArrivals, uploadArrival, reparseAllArrivals,
    getOlsParameters,
    getOlsLegacyByAerodrome,
    getObstaclesMeta, getObstaclesSwot, getObstaclesSof, uploadObstaclesSwot, uploadObstaclesSof,
    getObstaclesSwotBlocks, getObstaclesSwotBlock, getObstaclesSofBlocks, getObstaclesSofBlock,
    getClients, createClient, updateClient, deleteClient,
    getCoordinators, createCoordinator, updateCoordinator, deleteCoordinator
} from '../api/client';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { T } from '../assets/theme';
import { AIRSPACE_CLASS_META, classifyAirspace, styleForAirspace, formatAirspaceLimit } from '../utils/airspaceStyle';
import MapCoordinateViewer from '../components/MapCoordinateViewer';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// TILE_LAYERS constants for map backgrounds
const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

const TABS = [
    { key: 'airspace', label: 'Airspace',  icon: '🛂' },
    { key: 'arp',  label: 'ARP Table',   icon: '✈' },
    { key: 'circ', label: 'Circling',   icon: '🔄' },
    { key: 'clients', label: 'Clients',   icon: '🏢' },
    { key: 'cns',  label: 'CNS Table',   icon: '📡' },
    { key: 'geo',  label: 'Geo Borders', icon: '🌍' },
    { key: 'iac_ad', label: 'IAC_AD',    icon: '📍' },
    { key: 'ils',  label: 'ILS Procs',   icon: '📋' },
    { key: 'msa',  label: 'MSA Minima', icon: '🏔' },
    { key: 'oas_c',label: 'OAS Const',   icon: '📐' },
    { key: 'oas_t',label: 'OAS Temp',    icon: '📏' },
    { key: 'obstacles_ref', label: 'OBSTACLES', icon: '🗼' },
    { key: 'ols_params', label: 'OLS PARAMETERS', icon: '📖' },
    { key: 'proc', label: 'Procedures',  icon: '🛫' },
    { key: 'rmac', label: 'RMAC Areas',  icon: '📡' },
    { key: 'rnav', label: 'RNAV Routes', icon: '🛣️' },
    { key: 'rwy_profile', label: 'RWY Profile', icon: '📈' },
    { key: 'rwy',  label: 'RWY Table',   icon: '🛫' },
    { key: 'sid',  label: 'SID Departures', icon: '↗️' },
    { key: 'star', label: 'STAR Arrivals', icon: '↘️' },
    { key: 'thr',  label: 'THR Table',   icon: '🛬' },
    { key: 'waypoints', label: 'Waypoints', icon: '🧭' },
];


// ─── Column definitions ────────────────────────────────────────────────────────

const ARP_COLS = [
    { key: 'icao_desig', label: 'ICAO', w: 90, edit: true },
    { key: 'ad_name', label: 'Name', w: 240, edit: true },
    { key: 'type', label: 'Type', w: 180, edit: true },
    { key: 'arp_lat', label: 'Lat', w: 130, edit: true, num: true },
    { key: 'arp_lon', label: 'Lon', w: 130, edit: true, num: true },
    { key: 'adelev', label: 'Elev (ft)', w: 100, edit: true, num: true },
    { key: 'adg', label: 'ADG (Amdt 18)', w: 130, edit: false },
    { key: 'source', label: 'Source', w: 160, edit: false },
    { key: 'sourcedate', label: 'Source Date', w: 120, edit: false },
];

const CNS_COLS = [
    { key: 'ident', label: 'Ident', w: 90, edit: true },
    { key: 'name', label: 'Name', w: 220, edit: true },
    { key: 'type', label: 'Type', w: 140, edit: true },
    { key: 'feattype', label: 'Feat Type', w: 110, edit: false },
    { key: 'freq', label: 'Freq', w: 100, edit: true },
    { key: 'lat_cns', label: 'Lat', w: 130, edit: true, num: true },
    { key: 'lon_cns', label: 'Lon', w: 130, edit: true, num: true },
    { key: 'navaidelev', label: 'Elev (m)', w: 100, edit: true, num: true },
    { key: 'gnd_elev_m', label: 'Ground Elev (m)', w: 130, edit: true, num: true },
];

const THR_COLS = [
    { key: 'arptid', label: 'ICAO', w: 90, edit: true },
    { key: 'airfield', label: 'Aerodrome', w: 210, edit: true },
    { key: 'threshold', label: 'Threshold', w: 90, edit: true },
    { key: 'classification', label: 'Class', w: 80, edit: true },
    { key: 'thr_lat', label: 'THR Lat', w: 130, edit: true, num: true },
    { key: 'thr_lon', label: 'THR Lon', w: 130, edit: true, num: true },
    { key: 'elev_ft', label: 'Elev (ft)', w: 90, edit: true, num: true },
    { key: 'rwy', label: 'RWY', w: 90, edit: true },
    { key: 'derivation', label: 'Derivation', w: 120, edit: true },
    { key: 'source', label: 'Source', w: 200, edit: true },
];

const RWY_COLS = [
    { key: 'icao_desig', label: 'ICAO', w: 90, edit: true },
    { key: 'designator', label: 'Designator', w: 110, edit: true },
    { key: 'thrid_lo', label: 'THR Lo', w: 100, edit: true },
    { key: 'thrid_hi', label: 'THR Hi', w: 100, edit: true },
    { key: 'length_m', label: 'Length (m)', w: 100, edit: true, num: true },
    { key: 'width_m', label: 'Width (m)', w: 100, edit: true, num: true },
    { key: 'true_bearing_lo', label: 'True Bearing Lo', w: 120, edit: true, num: true },
    { key: 'source', label: 'Source', w: 220, edit: true },
];

const RWY_PROFILE_COLS = [
    { key: 'icao_desig', label: 'ICAO', w: 90, edit: false },
    { key: 'designator', label: 'Runway', w: 100, edit: false },
    { key: 'rwy_id', label: 'RWY ID', w: 70, edit: true, num: true },
    { key: 'point_seq', label: 'Seq', w: 60, edit: true, num: true },
    { key: 'distance_m', label: 'Dist (m)', w: 100, edit: true, num: true },
    { key: 'lat', label: 'Lat', w: 130, edit: true, num: true },
    { key: 'lon', label: 'Lon', w: 130, edit: true, num: true },
    { key: 'elev_ft', label: 'Elev (ft)', w: 90, edit: true, num: true },
    { key: 'elev_m', label: 'Elev (m)', w: 90, edit: true, num: true },
    { key: 'point_type', label: 'Type', w: 80, edit: true },
    { key: 'source', label: 'Source', w: 160, edit: true },
];

// IAC_AD — the master aerodrome list. An ICAO must exist here before an ILS
// procedure can be added for it in the ILS Procs tab (foreign key).
const IAC_AD_COLS = [
    { key: 'icao_code', label: 'ICAO', w: 90, edit: false },
    { key: 'name', label: 'Name', w: 260, edit: true },
    { key: 'city', label: 'City', w: 160, edit: true },
    { key: 'country', label: 'Country', w: 90, edit: true },
    { key: 'elevation_ft', label: 'Elev (ft)', w: 100, edit: true, num: true },
    { key: 'latitude', label: 'Lat', w: 130, edit: true, num: true },
    { key: 'longitude', label: 'Lon', w: 130, edit: true, num: true },
];

const ILS_PROC_COLS = [
    { key: 'id', label: 'ID', w: 60, edit: false },
    { key: 'icao_code', label: 'ICAO', w: 80, edit: true },
    { key: 'runway', label: 'Runway', w: 80, edit: true },
    { key: 'ils_category', label: 'CAT', w: 80, edit: true },
    { key: 'gp_vpa_deg', label: 'VPA', w: 80, edit: true, num: true },
    { key: 'loc_thr_dist_m', label: 'Loc Dist', w: 90, edit: true, num: true },
    { key: 'rdh_m', label: 'RDH', w: 80, edit: true, num: true },
    { key: 'course_width_thr_m', label: 'Course W', w: 100, edit: true, num: true },
    { key: 'aircraft_cat', label: 'AC Cat', w: 80, edit: true },
    { key: 'std_on', label: 'Std On', w: 80, edit: true },
    { key: 'wing_semi_span_m', w: 100, label: 'Wing Semi', edit: true, num: true },
    { key: 'gp_wheel_antenna_m', w: 100, label: 'GP Wheel', edit: true, num: true },
    { key: 'm_app_cg_pct', w: 100, label: 'M App CG', edit: true, num: true },
];

const OAS_CONST_COLS = [
    { key: 'id', label: 'ID', w: 60, edit: false },
    { key: 'ils_procedure_id', label: 'Proc ID', w: 80, edit: true, num: true },
    { key: 'icao_code', label: 'ICAO', w: 80, edit: false },
    { key: 'runway', label: 'RWY', w: 60, edit: false },
    { key: 'wa', label: 'wa', w: 80, edit: true, num: true },
    { key: 'wb', label: 'wb', w: 80, edit: true, num: true },
    { key: 'wc', label: 'wc', w: 80, edit: true, num: true },
    { key: 'xa', label: 'xa', w: 80, edit: true, num: true },
    { key: 'xb', label: 'xb', w: 80, edit: true, num: true },
    { key: 'xc', label: 'xc', w: 80, edit: true, num: true },
    { key: 'ya', label: 'ya', w: 80, edit: true, num: true },
    { key: 'yb', label: 'yb', w: 80, edit: true, num: true },
    { key: 'yc', label: 'yc', w: 80, edit: true, num: true },
    { key: 'za', label: 'za', w: 80, edit: true, num: true },
    { key: 'zb', label: 'zb', w: 80, edit: true, num: true },
    { key: 'zc', label: 'zc', w: 80, edit: true, num: true },
];

const OAS_TEMP_COLS = [
    { key: 'id', label: 'ID', w: 60, edit: false },
    { key: 'ils_procedure_id', label: 'Proc ID', w: 80, edit: true, num: true },
    { key: 'icao_code', label: 'ICAO', w: 80, edit: false },
    { key: 'runway', label: 'RWY', w: 60, edit: false },
    { key: 'height_level', label: 'Level', w: 80, edit: true },
    { key: 'cx', label: 'cx', w: 90, edit: true, num: true },
    { key: 'cy', label: 'cy', w: 90, edit: true, num: true },
    { key: 'dx', label: 'dx', w: 90, edit: true, num: true },
    { key: 'dy', label: 'dy', w: 90, edit: true, num: true },
    { key: 'ex', label: 'ex', w: 90, edit: true, num: true },
    { key: 'ey', label: 'ey', w: 90, edit: true, num: true },
];

const WAYPOINT_COLS = [
    { key: 'id', label: 'ID', w: 60, edit: false },
    { key: 'designator', label: 'Designator', w: 100, edit: true },
    { key: 'icao', label: 'ICAO', w: 90, edit: true },
    { key: 'latitude', label: 'Lat', w: 130, edit: true, num: true },
    { key: 'longitude', label: 'Lon', w: 130, edit: true, num: true },
    { key: 'aixm_interpretation', label: 'Interp.', w: 90, edit: true },
    { key: 'aixm_uuid', label: 'AIXM UUID', w: 220, edit: false },
    { key: 'created_at', label: 'Created', w: 150, edit: false },
];

const CIRCLING_MINIMA_COLS = [
    { key: 'icao_desig', label: 'ICAO', w: 100, edit: true },
    { key: 'cat_a', label: 'Cat A (ft)', w: 100, edit: true, num: true },
    { key: 'cat_b', label: 'Cat B (ft)', w: 100, edit: true, num: true },
    { key: 'cat_c', label: 'Cat C (ft)', w: 100, edit: true, num: true },
    { key: 'cat_d', label: 'Cat D (ft)', w: 100, edit: true, num: true },
];

// ─── Default empty forms per table ────────────────────────────────────────────
const ARP_DEFAULTS = { icao_desig: '', ad_name: '', type: '', arp_lat: '', arp_lon: '', adelev: '', source: '', sourcedate: '' };
const CNS_DEFAULTS = { ident: '', name: '', type: '', feattype: '', freq: '', lat_cns: '', lon_cns: '', navaidelev: '', gnd_elev_m: '' };
const THR_DEFAULTS = { arptid: '', airfield: '', threshold: '', classification: '', thr_lat: '', thr_lon: '', elev_ft: '', rwy: '', derivation: '', source: '' };
const RWY_DEFAULTS = { icao_desig: '', designator: '', thrid_lo: '', thrid_hi: '', length_m: '', width_m: '', true_bearing_lo: '', source: '' };
const RWY_PROFILE_DEFAULTS = { rwy_id: '', point_seq: '', distance_m: '', lat: '', lon: '', elev_ft: '', elev_m: '', point_type: '', source: '' };
const IAC_AD_DEFAULTS = { icao_code: '', name: '', city: '', country: '', elevation_ft: '', latitude: '', longitude: '' };
const WAYPOINT_DEFAULTS = { designator: '', icao: '', latitude: '', longitude: '', aixm_interpretation: 'BASELINE' };
const ILS_PROC_DEFAULTS = { icao_code: '', runway: '', ils_category: '', gp_vpa_deg: 3, loc_thr_dist_m: '', rdh_m: 15, course_width_thr_m: 210, aircraft_cat: 'A,B,C,D', std_on: true, wing_semi_span_m: 30, gp_wheel_antenna_m: 7, m_app_cg_pct: 3 };
const OAS_CONST_DEFAULTS = { ils_procedure_id: '', wa: '', wb: '', wc: '', xa: '', xb: '', xc: '', ya: '', yb: '', yc: '', za: '', zb: '', zc: '' };
const OAS_TEMP_DEFAULTS = { ils_procedure_id: '', height_level: '', cx: '', cy: '', dx: '', dy: '', ex: '', ey: '' };
const AIRSPACE_COLS = [
    { key: 'uuid', label: 'UUID', w: 220, edit: false },
    { key: 'designator', label: 'Designator', w: 120, edit: true },
    { key: 'name', label: 'Name', w: 220, edit: true },
    { key: 'type', label: 'Type', w: 100, edit: true },
    { key: 'designator_icao', label: 'ICAO', w: 90, edit: true },
    { key: 'upper_limit', label: 'Upper', w: 80, edit: true, num: true },
    { key: 'upper_limit_uom', label: 'Upper UOM', w: 90, edit: true },
    { key: 'upper_limit_ref', label: 'Upper Ref', w: 100, edit: true },
    { key: 'lower_limit', label: 'Lower', w: 80, edit: true, num: true },
    { key: 'lower_limit_uom', label: 'Lower UOM', w: 90, edit: true },
    { key: 'lower_limit_ref', label: 'Lower Ref', w: 100, edit: true },
    { key: 'frequency', label: 'Frequency', w: 220, edit: true },
    { key: 'interpretation', label: 'Interpretation', w: 120, edit: true },
];

const AIRSPACE_DEFAULTS = {
    designator: '', name: '', type: '', designator_icao: '', interpretation: 'BASELINE',
    upper_limit: '', upper_limit_uom: '', upper_limit_ref: '', lower_limit: '', lower_limit_uom: '', lower_limit_ref: '',
    frequency: '',
};

const CIRCLING_MINIMA_DEFAULTS = { icao_desig: '', cat_a: '', cat_b: '', cat_c: '', cat_d: '' };

const MSA_MINIMA_COLS = [
    { key: 'icao_code', label: 'ICAO', w: 120, edit: true },
    { key: 'taa_msa',   label: 'Lowest MSA/TAA (ft)', w: 180, edit: true, num: true },
];
const MSA_MINIMA_DEFAULTS = { icao_code: '', taa_msa: '' };

// ─── Shared small components ──────────────────────────────────────────────────
function Btn({ children, onClick, variant = 'primary', small, disabled, style: sx = {} }) {
    const variants = {
        primary: { bg: T.blue, color: T.white },
        ghost: { bg: 'rgba(58,138,196,0.09)', color: T.blue },
        success: { bg: 'rgba(46,201,139,0.15)', color: T.safe },
        danger: { bg: 'rgba(232,64,64,0.15)', color: '#e84040' },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button onClick={onClick} disabled={disabled} style={{
            background: v.bg, color: v.color, border: `1px solid ${v.color}33`,
            borderRadius: 6, padding: small ? '4px 10px' : '7px 16px',
            fontSize: small ? 11 : 12, fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', whiteSpace: 'nowrap', ...sx,
        }}>{children}</button>
    );
}

// ─── Inline editable cell ─────────────────────────────────────────────────────
function EditableCell({ value, editable, numeric, onChange }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value ?? '');

    if (!editable) return <span style={{ color: T.muted }}>{value ?? '—'}</span>;

    return editing ? (
        <input
            autoFocus
            type={numeric ? 'number' : 'text'}
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={() => {
                setEditing(false);
                if (String(val) !== String(value ?? '')) onChange(val);
            }}
            onKeyDown={e => {
                if (e.key === 'Enter') e.target.blur();
                if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); }
            }}
            style={{
                background: T.bg0, border: `1px solid ${T.blue}`, borderRadius: 4,
                padding: '2px 6px', color: T.white, fontSize: 12,
                width: '100%', minWidth: 60, outline: 'none', boxSizing: 'border-box',
            }}
        />
    ) : (
        <span
            onClick={() => setEditing(true)}
            title="Click to edit"
            style={{
                cursor: 'text', borderBottom: `1px dashed ${T.border}`,
                display: 'inline-block', minWidth: 30, lineHeight: '1.4',
            }}
        >
            {value ?? '—'}
        </span>
    );
}

// ─── Add New Row Modal ─────────────────────────────────────────────────────────
function AddModal({ tab, onClose, onAdded }) {
    const DEFAULTS = {
        arp: ARP_DEFAULTS, cns: CNS_DEFAULTS, thr: THR_DEFAULTS, rwy: RWY_DEFAULTS, rwy_profile: RWY_PROFILE_DEFAULTS,
        iac_ad: IAC_AD_DEFAULTS, waypoints: WAYPOINT_DEFAULTS,
        ils: ILS_PROC_DEFAULTS, oas_c: OAS_CONST_DEFAULTS, oas_t: OAS_TEMP_DEFAULTS,
        airspace: AIRSPACE_DEFAULTS, circ: CIRCLING_MINIMA_DEFAULTS, msa: MSA_MINIMA_DEFAULTS
    }[tab];
    const COLS = {
        arp: ARP_COLS, cns: CNS_COLS, thr: THR_COLS, rwy: RWY_COLS, rwy_profile: RWY_PROFILE_COLS,
        iac_ad: IAC_AD_COLS, waypoints: WAYPOINT_COLS,
        ils: ILS_PROC_COLS, oas_c: OAS_CONST_COLS, oas_t: OAS_TEMP_COLS,
        airspace: AIRSPACE_COLS, circ: CIRCLING_MINIMA_COLS, msa: MSA_MINIMA_COLS
    }[tab];

    const [form, setForm] = useState({ ...DEFAULTS });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    async function submit() {
        setLoading(true); setErr('');
        try {
            const creators = {
                arp: createArp, cns: createCns, thr: createThr, rwy: createRwy, rwy_profile: createRwyProfile,
                iac_ad: createAirport, waypoints: createWaypoint,
                ils: createIlsProc, oas_c: createOasConstant, oas_t: createOasTemplate,
                airspace: createAirspace, circ: createCirclingMinima, msa: createMsaMinima
            };
            const creator = creators[tab];
            // Filter out empty strings → null
            const payload = Object.fromEntries(
                Object.entries(form).filter(([, v]) => v !== '' && v !== null)
            );
            const { data } = await creator(payload);
            if (data.error) { setErr(data.error); return; }
            onAdded(data);
            onClose();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }

    // Which columns to show in the modal (editable ones only for CNS/ARP; all for THR)
    // icao_code is locked against inline table edits (it's the URL id) but
    // must still be entered when adding a new aerodrome, so IAC_AD shows
    // every column here regardless of its edit flag.
    const formCols = tab === 'iac_ad' ? COLS : COLS.filter(c => c.edit !== false);

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600,
        }}>
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: 28, width: 540, maxHeight: '85vh', overflowY: 'auto',
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>
                    Add New {tab.toUpperCase()} Record
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {formCols.map(col => (
                        <div key={col.key} style={{ gridColumn: (col.w || 0) >= 200 ? '1 / -1' : 'auto' }}>
                            <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                                {col.label}
                            </div>
                            <input
                                type={col.num ? 'number' : 'text'}
                                value={form[col.key] ?? ''}
                                onChange={e => set(col.key, e.target.value)}
                                style={inputStyle}
                                placeholder={col.label}
                            />
                        </div>
                    ))}
                </div>
                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>{loading ? 'Saving…' : 'Add Record'}</Btn>
                </div>
            </div>
        </div>
    );
}

// ─── Map View Overlay ─────────────────────────────────────────────────────────
function DataMapModal({ onClose }) {
    const mapRef = useRef(null), mapInst = useRef(null), tileRef = useRef(null), layersRef = useRef([]);
    const layerGroupsRef = useRef({});
    const infoPanelRef = useRef(null), infoBadgeRef = useRef(null), infoNameRef = useRef(null), 
          infoDescRef = useRef(null), infoCoordsRef = useRef(null);
    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [data, setData] = useState({ arps: [], cns: [], thrs: [], airspaces: [], geo_borders: [], rnav_routes: [] });
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState({ arp: true, cns: true, thr: true, airspace: true, geo: true, rnav: true });
    const [visibleClasses, setVisibleClasses] = useState(new Set(Object.keys(AIRSPACE_CLASS_META)));
    const [airspaceCount, setAirspaceCount] = useState({});

    useEffect(() => {
        Promise.all([
            getMapData(),
            getGeoBorders(true, true), // Get active geo borders with geometry
            getRnavRoutes()
        ]).then(([mapRes, geoRes, rnavRes]) => {
            const mapPayload = mapRes.data || { arps: [], cns: [], thrs: [], airspaces: [] };
            mapPayload.geo_borders = geoRes.data || [];
            mapPayload.rnav_routes = rnavRes.data || [];
            
            // Count airspaces by class
            const counts = {};
            (mapPayload.airspaces || []).forEach(a => {
                const cls = classifyAirspace(a.name || '');
                counts[cls] = (counts[cls] || 0) + 1;
            });
            setAirspaceCount(counts);
            
            setData(mapPayload);
            setLoading(false);
        }).catch(err => {
            console.error("Failed to load map data", err);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        
        // Initialize airspace layer groups
        Object.keys(AIRSPACE_CLASS_META).forEach(cls => {
            layerGroupsRef.current[cls] = L.layerGroup().addTo(mapInst.current);
        });
        
        // Initialize info panel refs
        infoPanelRef.current = { element: null, badge: null, name: null, desc: null, coords: null };
        setMapState(mapInst.current);

        return () => {
            Object.values(layerGroupsRef.current).forEach(lg => mapInst.current?.removeLayer(lg));
            mapInst.current?.remove();
            mapInst.current = null;
            setMapState(null);
        };
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const l = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    useEffect(() => {
        if (!mapInst.current || loading) return;
        
        // Clear all non-airspace layers
        layersRef.current.forEach(l => {
            if (mapInst.current && l) {
                try { mapInst.current.removeLayer(l); } catch (e) {}
            }
        });
        layersRef.current = [];
        
        // Clear airspace layer groups and sync their map attachment with visibleClasses
        Object.entries(layerGroupsRef.current).forEach(([cls, lg]) => {
            if (!lg || !mapInst.current) return;
            lg.clearLayers();
            const shouldShow = visibleClasses.has(cls);
            if (shouldShow && !mapInst.current.hasLayer(lg)) {
                mapInst.current.addLayer(lg);
            } else if (!shouldShow && mapInst.current.hasLayer(lg)) {
                mapInst.current.removeLayer(lg);
            }
        });
        
        const bounds = [];

        if (visible.arp) {
            data.arps.forEach(a => {
                if (!a.arp_lat || !a.arp_lon || !a.icao_desig) return;
                const ll = [a.arp_lat, a.arp_lon]; bounds.push(ll);
                const m = L.circleMarker(ll, { radius: 6, color: '#3a8ac4', fillColor: '#3a8ac4', fillOpacity: 0.8, weight: 1 })
                    .bindTooltip(`<b>✈ ${a.icao_desig}</b><br/>${a.ad_name}`);
                m.addTo(mapInst.current); layersRef.current.push(m);
            });
        }
        if (visible.cns) {
            data.cns.forEach(c => {
                if (!c.lat_cns || !c.lon_cns) return;
                const ident = c.ident || c.name || "";
                if (!ident) return;
                const ll = [c.lat_cns, c.lon_cns]; bounds.push(ll);
                const m = L.circleMarker(ll, { radius: 5, color: '#f5a623', fillColor: '#f5a623', fillOpacity: 0.8, weight: 1 })
                    .bindTooltip(`<b>📡 ${ident}</b><br/>${c.type || c.feattype || 'N/A'}`);
                m.addTo(mapInst.current); layersRef.current.push(m);
            });
        }
        if (visible.thr) {
            data.thrs.forEach(t => {
                if (!t.thr_lat || !t.thr_lon) return;
                const label = t.threshold || t.rwy || '';
                if (!label) return;
                const ll = [t.thr_lat, t.thr_lon]; bounds.push(ll);
                const m = L.circleMarker(ll, { radius: 4, color: '#e84040', fillColor: '#e84040', fillOpacity: 0.8, weight: 1 })
                    .bindTooltip(`<b>🛬 ${label}</b><br/>${t.arptid || t.airfield}`);
                m.addTo(mapInst.current); layersRef.current.push(m);
            });
        }
        const parseGeometry = (geom) => {
            if (!geom) return null;
            if (typeof geom === 'string') {
                try { return JSON.parse(geom); } catch (_) { return null; }
            }
            return geom;
        };

        if (visible.airspace && data.airspaces) {
            data.airspaces.forEach(a => {
                const geometry = parseGeometry(a.geometry);
                if (!geometry || geometry.type !== 'Polygon') return;
                
                const upper = formatAirspaceLimit(a.upper_limit, a.upper_limit_uom, a.upper_limit_ref);
                const lower = formatAirspaceLimit(a.lower_limit, a.lower_limit_uom, a.lower_limit_ref);
                const descLines = [`${a.type || '—'}`, `Upper: ${upper}`, `Lower: ${lower}`];
                if (a.frequency) descLines.push(a.frequency);
                const feature = { name: a.name || '', designator: a.designator || '', description: descLines.join('\n') };
                const sty = styleForAirspace(feature);
                
                try {
                    const coords = geometry.coordinates[0].map(c => [c[1], c[0]]); // GeoJSON [lng,lat] → Leaflet [lat,lng]
                    const layer = L.polygon(coords, { ...sty, interactive: true });
                    
                    // Hover highlight
                    layer.on('mouseover', function (e) {
                        this.setStyle({ weight: 2.5, fillOpacity: Math.min(sty.fillOpacity + 0.15, 0.6) });
                        showInfoPanelAirspace(feature, sty, e.latlng);
                    });
                    
                    // Track cursor inside polygon
                    layer.on('mousemove', function (e) {
                        updateInfoCoordsAirspace(e.latlng);
                    });
                    
                    // Hover end
                    layer.on('mouseout', function () {
                        this.setStyle({ weight: sty.weight, fillOpacity: sty.fillOpacity });
                        hideInfoPanelAirspace();
                    });
                    
                    // Click popup
                    layer.on('click', function (e) {
                        L.popup({ className: 'airspace-popup' })
                            .setLatLng(e.latlng)
                            .setContent(`<b>${feature.name}</b><br/><small style="color:#999">${sty._class}</small><br/><span style="font-size:11px">${feature.description.replace(/\n/g, '<br/>')}</span>`)
                            .openOn(mapInst.current);
                    });
                    
                    // Sticky tooltip
                    layer.bindTooltip(feature.name || 'Airspace', {
                        permanent: false,
                        sticky: true,
                        direction: 'top',
                        className: 'airspace-tt',
                    });
                    
                    layerGroupsRef.current[sty._class].addLayer(layer);
                    layersRef.current.push(layer);
                    bounds.push(L.latLngBounds(coords));
                } catch (e) {
                    console.error("Failed to parse airspace geometry", a.uuid, e);
                }
            });
        }
        if (visible.geo && data.geo_borders) {
            data.geo_borders.forEach(b => {
                if (!b.geometry) return;
                try {
                    const geojsonLayer = L.geoJSON(b.geometry, {
                        style: {
                            color: '#2ea44f', weight: 2, fillOpacity: 0.1, dashArray: '4, 4'
                        },
                        interactive: false
                    });
                    geojsonLayer.addTo(mapInst.current);
                    layersRef.current.push(geojsonLayer);
                    bounds.push(geojsonLayer.getBounds());
                } catch (e) {
                    console.error("Failed to parse geojson for border", b.id);
                }
            });
        }
        if (visible.rnav && data.rnav_routes) {
            data.rnav_routes.forEach(r => {
                const latlngs = r.json_geometry;
                if (!latlngs || latlngs.length < 2) return;
                const poly = L.polyline(latlngs, { color: '#00e1ff', weight: 3, opacity: 0.8 })
                    .bindTooltip(`<b>🛣️ Route: ${r.route_name}</b><br/>Waypoints: ${r.point_count}`, { sticky: true });
                poly.addTo(mapInst.current);
                layersRef.current.push(poly);
                latlngs.forEach(pt => bounds.push(pt));
            });
        }

        if (bounds.length) {
            const latLngBounds = L.latLngBounds(
                bounds.map(b => b instanceof L.LatLngBounds ? [b.getSouthWest(), b.getNorthEast()] : b)
            );
            mapInst.current.fitBounds(latLngBounds, { padding: [40, 40], maxZoom: 14 });
        }
    }, [data, loading, visible, visibleClasses]);

    // Info panel functions for airspace hover
    const showInfoPanelAirspace = (feature, sty, latlng) => {
        if (!infoPanelRef.current.element) {
            infoPanelRef.current.element = document.getElementById('airspace-info-panel');
            infoPanelRef.current.badge = document.getElementById('airspace-info-badge');
            infoPanelRef.current.name = document.getElementById('airspace-info-name');
            infoPanelRef.current.desc = document.getElementById('airspace-info-desc');
            infoPanelRef.current.coords = document.getElementById('airspace-info-coords');
        }
        if (!infoPanelRef.current.element) return;
        
        const meta = AIRSPACE_CLASS_META[sty._class] || AIRSPACE_CLASS_META['Other'];
        infoPanelRef.current.badge.textContent = sty._class;
        infoPanelRef.current.badge.style.color = meta.color;
        infoPanelRef.current.badge.style.background = meta.fillColor + '55';
        infoPanelRef.current.badge.style.borderColor = meta.color + '88';
        infoPanelRef.current.name.textContent = feature.name;
        infoPanelRef.current.desc.textContent = feature.description || '—';
        infoPanelRef.current.coords.textContent = `${latlng.lat.toFixed(5)}°N  ${Math.abs(latlng.lng).toFixed(5)}°${latlng.lng < 0 ? 'W' : 'E'}`;
        infoPanelRef.current.element.classList.add('visible');
    };

    const updateInfoCoordsAirspace = (latlng) => {
        if (infoPanelRef.current.coords) {
            infoPanelRef.current.coords.textContent = `${latlng.lat.toFixed(5)}°N  ${Math.abs(latlng.lng).toFixed(5)}°${latlng.lng < 0 ? 'W' : 'E'}`;
        }
    };

    const hideInfoPanelAirspace = () => {
        if (infoPanelRef.current.element) {
            infoPanelRef.current.element.classList.remove('visible');
        }
    };

    const toggleAirspaceClass = (cls) => {
        // Map attachment for the class layer group is synced from visibleClasses
        // by the main render effect — no direct map mutation needed here.
        setVisibleClasses(prev => {
            const next = new Set(prev);
            next.has(cls) ? next.delete(cls) : next.add(cls);
            return next;
        });
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', zIndex: 1000, padding: 30 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <h2 style={{ margin: 0, color: T.white, fontSize: 22 }}>🗺 Data Manager Map View</h2>
                    <div style={{ display: 'flex', gap: 12, background: T.bg1, padding: '6px 16px', borderRadius: 8, border: `1px solid ${T.border}` }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.white, cursor: 'pointer' }}>
                            <input type="checkbox" checked={visible.arp} onChange={e => setVisible(v => ({ ...v, arp: e.target.checked }))} style={{ accentColor: '#3a8ac4' }} /> ✈ Aerodromes (FAOR)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.white, cursor: 'pointer' }}>
                            <input type="checkbox" checked={visible.cns} onChange={e => setVisible(v => ({ ...v, cns: e.target.checked }))} style={{ accentColor: '#f5a623' }} /> 📡 NAVAIDs (CTV-CVOR)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.white, cursor: 'pointer' }}>
                            <input type="checkbox" checked={visible.thr} onChange={e => setVisible(v => ({ ...v, thr: e.target.checked }))} style={{ accentColor: '#e84040' }} /> 🛬 Thresholds (RWY07)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.white, cursor: 'pointer' }}>
                            <input type="checkbox" checked={visible.airspace} onChange={e => setVisible(v => ({ ...v, airspace: e.target.checked }))} style={{ accentColor: '#7f5afd' }} /> 🛂 Airspace
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.white, cursor: 'pointer' }}>
                            <input type="checkbox" checked={visible.geo} onChange={e => setVisible(v => ({ ...v, geo: e.target.checked }))} style={{ accentColor: '#2ea44f' }} /> 🌍 Active Borders
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.white, cursor: 'pointer' }}>
                            <input type="checkbox" checked={visible.rnav} onChange={e => setVisible(v => ({ ...v, rnav: e.target.checked }))} style={{ accentColor: '#00e1ff' }} /> 🛣️ RNAV Routes
                        </label>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 6, background: T.bg1, padding: 4, borderRadius: 6, border: `1px solid ${T.border}` }}>
                        {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                            <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 12, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                        ))}
                    </div>
                    <button onClick={onClose} style={{ background: '#e84040', color: '#fff', border: 'none', borderRadius: 6, padding: '0 20px', fontWeight: 800, cursor: 'pointer' }}>✕ Close Map</button>
                </div>
            </div>
            <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden', border: `2px solid ${T.border}`, position: 'relative', background: '#0a192f' }}>
                {loading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 1000, color: T.white, fontWeight: 700 }}>Loading global coordinates...</div>}
                <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                <MapCoordinateViewer map={mapState} />

                {/* Airspace Info Panel */}
                <div id="airspace-info-panel" style={{
                    position: 'absolute', bottom: 36, left: 16, zIndex: 1000, width: 300,
                    background: 'linear-gradient(160deg, rgba(10, 22, 40, 0.97), rgba(13, 31, 60, 0.97))',
                    border: '1px solid rgba(0, 120, 200, 0.4)', borderRadius: 8, padding: '14px 16px',
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)', pointerEvents: 'none',
                    opacity: 0, transition: 'opacity 0.2s ease',
                }}>
                    <span id="airspace-info-badge" style={{
                        display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                        textTransform: 'uppercase', padding: '2px 8px', borderRadius: 3, marginBottom: 8, border: '1px solid',
                    }} />
                    <h2 id="airspace-info-name" style={{ fontSize: 13, fontWeight: 700, color: '#e8f4ff', marginBottom: 6, lineHeight: 1.3 }} />
                    <p id="airspace-info-desc" style={{ fontSize: 12, color: '#8ab8d8', lineHeight: 1.6, whiteSpace: 'pre-line', margin: 0 }} />
                    <p id="airspace-info-coords" style={{ fontSize: 10, color: '#3a6888', marginTop: 8, fontFamily: 'monospace', margin: '8px 0 0 0' }} />
                    <style>{`
                        #airspace-info-panel.visible { opacity: 1 !important; }
                    `}</style>
                </div>

                {/* Airspace Legend Panel */}
                <div style={{
                    position: 'absolute', top: 16, right: 10, zIndex: 1000,
                    background: 'rgba(10, 22, 40, 0.94)', border: '1px solid rgba(0, 120, 200, 0.35)',
                    borderRadius: 8, padding: '10px 14px', minWidth: 168, boxShadow: '0 2px 16px rgba(0, 0, 0, 0.4)',
                    pointerEvents: 'all',
                }}>
                    <h3 style={{ fontSize: 10, fontWeight: 700, color: '#6a9fc8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8, margin: 0 }}>
                        Airspace Class
                    </h3>
                    {Object.entries(AIRSPACE_CLASS_META)
                        .filter(([cls]) => airspaceCount[cls] > 0)
                        .map(([cls, meta]) => (
                            <div
                                key={cls}
                                onClick={() => toggleAirspaceClass(cls)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '3px 4px',
                                    borderRadius: 4, cursor: 'pointer', userSelect: 'none',
                                    transition: 'background 0.15s', marginBottom: 4,
                                    opacity: visibleClasses.has(cls) ? 1 : 0.35,
                                }}
                                onMouseEnter={e => e.target.style.background = 'rgba(0, 120, 200, 0.1)'}
                                onMouseLeave={e => e.target.style.background = 'transparent'}
                            >
                                <span style={{
                                    width: 14, height: 14, borderRadius: 3, border: `2px solid ${meta.color}`,
                                    background: meta.fillColor + '40', flexShrink: 0,
                                }} />
                                <span style={{ fontSize: 11, color: '#c8dff0', flex: 1 }}>{cls}</span>
                                <span style={{ fontSize: 10, color: '#3a6888' }}>{airspaceCount[cls] || 0}</span>
                            </div>
                        ))
                    }
                    <style>{`
                        .airspace-tt {
                            background: rgba(10, 22, 40, 0.92) !important;
                            border: 1px solid rgba(0, 120, 200, 0.5) !important;
                            border-radius: 5px !important;
                            color: #c8e8ff !important;
                            font-size: 11.5px !important;
                            font-weight: 600 !important;
                            padding: 4px 9px !important;
                            white-space: nowrap !important;
                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
                        }
                        .airspace-tt::before {
                            border-top-color: rgba(0, 120, 200, 0.5) !important;
                        }
                        .airspace-popup {
                            color: #fff;
                        }
                        .airspace-popup .leaflet-popup-content {
                            background: rgba(10, 22, 40, 0.95);
                            border-radius: 6px;
                            color: #c8e8ff;
                            font-size: 12px;
                        }
                    `}</style>
                </div>
            </div>
        </div>
    );
}

// ─── Data table with horizontal scroll ────────────────────────────────────────
function DataTable({ columns, rows, idCol, onEdit, onDelete }) {
    const [sortCol, setSortCol] = useState(null);
    const [sortDesc, setSortDesc] = useState(false);
    const [filters, setFilters] = useState({});

    const displayRows = useMemo(() => {
        let arr = [...rows];
        Object.entries(filters).forEach(([k, v]) => {
            if (!v) return;
            const term = v.toLowerCase();
            arr = arr.filter(r => String(r[k] || '').toLowerCase().includes(term));
        });
        if (sortCol) {
            arr.sort((a, b) => {
                let va = a[sortCol] ?? '', vb = b[sortCol] ?? '';
                if (typeof va === 'number' && typeof vb === 'number') return sortDesc ? vb - va : va - vb;
                va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
                if (va < vb) return sortDesc ? 1 : -1;
                if (va > vb) return sortDesc ? -1 : 1;
                return 0;
            });
        }
        return arr;
    }, [rows, sortCol, sortDesc, filters]);

    const handleSort = (key) => {
        if (sortCol === key) {
            if (sortDesc) { setSortCol(null); setSortDesc(false); }
            else setSortDesc(true);
        } else {
            setSortCol(key); setSortDesc(false);
        }
    };

    return (
        <div style={{
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 300px)',
            // Custom scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: `${T.border} ${T.bg0}`,
        }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr style={{ background: T.bg0 }}>
                        {columns.map(c => (
                            <th key={c.key} style={{
                                padding: '6px 12px', textAlign: 'left',
                                fontSize: 10, fontWeight: 700, color: T.white,
                                width: c.w, minWidth: c.w, maxWidth: c.w,
                                borderBottom: `1px solid ${T.border}`,
                                verticalAlign: 'top'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }} onClick={() => handleSort(c.key)}>
                                    <span>{c.label} {sortCol === c.key ? (sortDesc ? '▼' : '▲') : ''}</span>
                                </div>
                                <input
                                    placeholder="Filter..."
                                    value={filters[c.key] || ''}
                                    onChange={e => setFilters(f => ({ ...f, [c.key]: e.target.value }))}
                                    style={{ width: '100%', padding: '4px 6px', fontSize: 10, background: 'rgba(255,255,255,0.05)', color: T.white, border: `1px solid ${T.border}`, borderRadius: 4, outline: 'none' }}
                                />
                            </th>
                        ))}
                        {onDelete && (
                            <th style={{
                                padding: '10px 12px', width: 50, minWidth: 50,
                                borderBottom: `1px solid ${T.border}`,
                            }} />
                        )}
                    </tr>
                </thead>
                <tbody>
                    {displayRows.map((row, ri) => (
                        <tr key={ri} style={{
                            borderBottom: `1px solid ${T.border}22`,
                            background: ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)',
                        }}>
                            {columns.map(c => (
                                <td key={c.key} style={{
                                    padding: '8px 12px', color: T.white,
                                    width: c.w, minWidth: c.w, maxWidth: c.w,
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <EditableCell
                                        value={row[c.key]}
                                        editable={!!c.edit && !!onEdit}
                                        numeric={!!c.num}
                                        onChange={v => onEdit(row[idCol], c.key, v)}
                                    />
                                </td>
                            ))}
                            {onDelete && (
                                <td style={{ padding: '6px 8px', textAlign: 'center', width: 50 }}>
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Delete this record?')) onDelete(row[idCol]);
                                        }}
                                        style={{
                                            background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030',
                                            color: '#e84040', borderRadius: 4, padding: '2px 8px',
                                            fontSize: 11, cursor: 'pointer',
                                        }}
                                    >✕</button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Table view per tab ────────────────────────────────────────────────────────
function TableView({ tab }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);

    const fetchers = {
        arp: getArpData, cns: getCnsData, thr: getThrData, rwy: getRwyData, rwy_profile: getRwyProfileData,
        iac_ad: getAirportsData, waypoints: getWaypoints,
        ils: getIlsProcs, oas_c: getOasConstants, oas_t: getOasTemplates,
        airspace: getAirspaceData, circ: getCirclingMinimaData, msa: getMsaMinimaData
    };
    const columnsArr = {
        arp: ARP_COLS, cns: CNS_COLS, thr: THR_COLS, rwy: RWY_COLS, rwy_profile: RWY_PROFILE_COLS,
        iac_ad: IAC_AD_COLS, waypoints: WAYPOINT_COLS,
        ils: ILS_PROC_COLS, oas_c: OAS_CONST_COLS, oas_t: OAS_TEMP_COLS,
        airspace: AIRSPACE_COLS, circ: CIRCLING_MINIMA_COLS, msa: MSA_MINIMA_COLS
    };
    const idCols = {
        arp: 'ogc_fid', cns: 'ogc_fid', thr: 'ogc_fid', rwy: 'ogc_fid', rwy_profile: 'ogc_fid',
        iac_ad: 'icao_code', waypoints: 'id',
        ils: 'id', oas_c: 'id', oas_t: 'id', airspace: 'uuid', circ: 'icao_desig', msa: 'icao_code'
    };
    const deleters = {
        arp: deleteArp, cns: deleteCns, thr: deleteThr, rwy: deleteRwy, rwy_profile: deleteRwyProfile,
        iac_ad: deleteAirport, waypoints: deleteWaypoint,
        ils: deleteIlsProc, oas_c: deleteOasConstant, oas_t: deleteOasTemplate,
        airspace: deleteAirspace, circ: deleteCirclingMinima, msa: deleteMsaMinima
    };

    const fetcher = fetchers[tab];
    const columns = columnsArr[tab];
    const idCol = idCols[tab];
    const deleter = deleters[tab];

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await fetcher(query || undefined, page);
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } finally { setLoading(false); }
    }, [tab, query, page]);

    useEffect(() => { load(); }, [load]);

    async function handleEdit(id, key, value) {
        try {
            const updaters = {
                arp: updateArp, cns: updateCns, thr: updateThr, rwy: updateRwy, rwy_profile: updateRwyProfile,
                iac_ad: updateAirport, waypoints: updateWaypoint,
                ils: updateIlsProc, oas_c: updateOasConstant, oas_t: updateOasTemplate,
                airspace: updateAirspace, circ: updateCirclingMinima, msa: updateMsaMinima
            };
            const updater = updaters[tab];
            if (!updater) return;
            await updater(id, { [key]: value });
            setRows(prev => prev.map(r => r[idCol] === id ? { ...r, [key]: value } : r));
            setSaved('✓ Saved'); setTimeout(() => setSaved(''), 2000);
        } catch { setSaved('✕ Failed'); setTimeout(() => setSaved(''), 2000); }
    }

    async function handleDelete(id) {
        try {
            await deleter(id);
            setRows(prev => prev.filter(r => r[idCol] !== id));
            setTotal(t => t - 1);
            setSaved('✓ Deleted'); setTimeout(() => setSaved(''), 2000);
        } catch { setSaved('✕ Delete failed'); setTimeout(() => setSaved(''), 2000); }
    }

    function handleAdded(newRow) {
        setRows(prev => [newRow, ...prev]);
        setTotal(t => t + 1);
        setSaved('✓ Record added'); setTimeout(() => setSaved(''), 2000);
    }

    const pages = Math.ceil(total / 50);
    const tabLabels = {
        arp: 'ARP', cns: 'CNS', thr: 'THR', rwy: 'RWY', rwy_profile: 'RWY Profile', iac_ad: 'Aerodrome',
        waypoints: 'Waypoint',
        ils: 'ILS',
        oas_c: 'OAS Const', oas_t: 'OAS Temp', airspace: 'Airspace', geo: 'Geo Border',
        proc: 'Procedure', rmac: 'RMAC', circ: 'Circling', msa: 'MSA Minima'
    };
    const tabLabel = tabLabels[tab] || tab.toUpperCase();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { setQuery(search); setPage(1); } }}
                    placeholder={`Search by ${tab === 'cns' ? 'name' : tab === 'waypoints' ? 'designator/ICAO' : 'ICAO'}…`}
                    style={{
                        flex: '1 1 200px', background: T.bg0, border: `1px solid ${T.border}`,
                        borderRadius: 6, padding: '8px 12px', color: T.white, fontSize: 12, outline: 'none',
                    }}
                />
                <Btn small onClick={() => { setQuery(search); setPage(1); }}>🔍 Search</Btn>
                {query && <Btn small variant="ghost" onClick={() => { setSearch(''); setQuery(''); setPage(1); }}>× Clear</Btn>}
                <Btn small variant="success" onClick={() => setShowAdd(true)}>+ Add {tabLabel}</Btn>
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>
                    {total.toLocaleString()} records
                </span>
            </div>

            {/* Table container */}
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden',
            }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>
                        No records found.{' '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={() => setShowAdd(true)}>
                            Add a record →
                        </span>
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        rows={rows}
                        idCol={idCol}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                )}
            </div>

            {/* Pagination */}
            {pages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Btn small variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</Btn>
                    <span style={{ fontSize: 12, color: T.muted }}>Page {page} of {pages}</span>
                    <Btn small variant="ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</Btn>
                </div>
            )}

            {/* Add modal */}
            {showAdd && (
                <AddModal tab={tab} onClose={() => setShowAdd(false)} onAdded={handleAdded} />
            )}
        </div>
    );
}

// ─── Geo Borders View ──────────────────────────────────────────────────────────
function GeoBordersView() {
    const [borders, setBorders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saved, setSaved] = useState('');

    useEffect(() => {
        getGeoBorders().then(res => {
            setBorders(res.data || []);
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    }, []);

    async function handleToggle(id, currentActive) {
        try {
            const desired = !currentActive;
            await toggleGeoBorder(id, desired);
            setBorders(prev => prev.map(b => b.id === id ? { ...b, is_active: desired } : b));
            setSaved(`✓ Saved`);
            setTimeout(() => setSaved(''), 2000);
        } catch (err) {
            setSaved('✕ Failed');
            setTimeout(() => setSaved(''), 2000);
        }
    }

    if (loading) return <div style={{ color: T.muted, padding: 30 }}>Loading borders...</div>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12, color: T.muted }}>
                    Toggle geographic continent borders to show on the preview maps.
                </div>
                {saved && <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>}
            </div>

            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10,
                maxHeight: 'calc(100vh - 300px)', overflowY: 'auto'
            }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.bg0 }}>
                        <tr>
                            <th style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, color: T.white, fontWeight: 700 }}>Border Region</th>
                            <th style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}`, color: T.white, fontWeight: 700, width: 120 }}>Active on Map</th>
                        </tr>
                    </thead>
                    <tbody>
                        {borders.map((b, i) => (
                            <tr key={b.id} style={{ borderBottom: `1px solid ${T.border}22`, background: i % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)' }}>
                                <td style={{ padding: '10px 16px', color: T.white }}>{b.name || 'Unnamed Border'}</td>
                                <td style={{ padding: '10px 16px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!b.is_active}
                                            onChange={() => handleToggle(b.id, !!b.is_active)}
                                            style={{ accentColor: T.safe, width: 16, height: 16, cursor: 'pointer' }}
                                        />
                                    </label>
                                </td>
                            </tr>
                        ))}
                        {borders.length === 0 && (
                            <tr>
                                <td colSpan={2} style={{ padding: 30, textAlign: 'center', color: T.muted }}>No geo borders found in the database. Ensure the SQL has been imported.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Add Procedure Modal ───────────────────────────────────────────────────────
function AddProcedureModal({ onClose, onAdded }) {
    const PROC_TYPES = ['RNP LNAV', 'RNP AR', 'ILS', 'VOR', 'NDB'];
    const [form, setForm] = useState({
        procedure_name: '', airport_icao: '', runway: '',
        procedure_type: 'RNP LNAV',
        initial_moca_ft: '', intermediate_moca_ft: '', final_oca_ft: '', hold_ft: '',
    });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    async function handleFileChange(e) {
        const f = e.target.files[0];
        if (!f) return;
        setFile(f);
        // Auto-populate procedure name from filename stem
        if (!form.procedure_name) {
            const stem = f.name.replace(/\.kmz$/i, '');
            set('procedure_name', stem);
        }
    }

    async function submit() {
        if (!file) { setErr('Please select a .kmz file'); return; }
        if (!form.procedure_name.trim()) { setErr('Procedure Name is required'); return; }
        setLoading(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
            const { data } = await uploadProcedure(fd);
            if (data.error) { setErr(data.error); return; }
            onAdded(data);
            onClose();
        } catch (e) {
            setErr(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600,
        }}>
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>🛫 Add Procedure</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                    {/* KMZ File */}
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>KMZ File *</div>
                        <input type="file" accept=".kmz" onChange={handleFileChange}
                            style={{ ...inputStyle, padding: '5px 10px', cursor: 'pointer' }} />
                    </div>

                    {/* Procedure Name */}
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Procedure Name *</div>
                        <input type="text" value={form.procedure_name} onChange={e => set('procedure_name', e.target.value)}
                            style={inputStyle} placeholder="e.g. FAPE RNP (LNAV) RWY 08" />
                    </div>

                    {/* Airport ICAO */}
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Airport ICAO *</div>
                        <input type="text" value={form.airport_icao} onChange={e => set('airport_icao', e.target.value.toUpperCase().slice(0, 4))}
                            style={inputStyle} maxLength={4} placeholder="FAPE" />
                    </div>

                    {/* Runway */}
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Runway *</div>
                        <input type="text" value={form.runway} onChange={e => set('runway', e.target.value)}
                            style={inputStyle} placeholder="RWY 08" />
                    </div>

                    {/* Procedure Type */}
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Procedure Type</div>
                        <select value={form.procedure_type} onChange={e => set('procedure_type', e.target.value)}
                            style={{ ...inputStyle, cursor: 'pointer' }}>
                            {PROC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    {/* Minima */}
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Initial MOCA (ft)</div>
                        <input type="text" value={form.initial_moca_ft} onChange={e => set('initial_moca_ft', e.target.value)}
                            style={inputStyle} placeholder="2000" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Intermediate MOCA (ft)</div>
                        <input type="text" value={form.intermediate_moca_ft} onChange={e => set('intermediate_moca_ft', e.target.value)}
                            style={inputStyle} placeholder="2000 or 7600;7700;7100 for step-downs" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Final OCA (ft)</div>
                        <input type="text" value={form.final_oca_ft} onChange={e => set('final_oca_ft', e.target.value)}
                            style={inputStyle} placeholder="750, or 4930;4510 — last value is the OCA" />
                    </div>
                    <div style={{ fontSize: 10, color: T.muted, gridColumn: '1 / -1', marginTop: -6 }}>
                        Separate multiple values with <code>;</code> in flight order when a segment has a step-down fix (e.g. Intermediate <code>7600;7700;7100</code>). For Final, the last value is the OCA.
                    </div>

                    {/* TAA sector altitudes */}
                    <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                        <div style={{ fontSize: 10, color: '#9B59B6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontWeight: 700 }}>
                            ✈ TAA Lower Limits (ft)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                            <div>
                                <div style={{ fontSize: 9, color: T.muted, marginBottom: 3 }}>Straight-In</div>
                                <input type="text" value={form.taa_si_ft || ''} onChange={e => set('taa_si_ft', e.target.value)}
                                    style={inputStyle} placeholder="e.g. 4300 or 7300;8200" />
                            </div>
                            <div>
                                <div style={{ fontSize: 9, color: T.muted, marginBottom: 3 }}>Left Base</div>
                                <input type="text" value={form.taa_lb_ft || ''} onChange={e => set('taa_lb_ft', e.target.value)}
                                    style={inputStyle} placeholder="e.g. 5000 or 7300;8200" />
                            </div>
                            <div>
                                <div style={{ fontSize: 9, color: T.muted, marginBottom: 3 }}>Right Base</div>
                                <input type="text" value={form.taa_rb_ft || ''} onChange={e => set('taa_rb_ft', e.target.value)}
                                    style={inputStyle} placeholder="e.g. 2200 or 7300;8200" />
                            </div>
                        </div>
                    </div>

                    {/* Holding */}
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Minimum Holding Level (ft)</div>
                        <input type="number" value={form.hold_ft || ''} onChange={e => set('hold_ft', e.target.value)}
                            style={inputStyle} placeholder="e.g. 8600" />
                    </div>
                </div>

                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>
                        {loading ? <span>⏳ Uploading…</span> : '⬆ Upload'}
                    </Btn>
                </div>
            </div>
        </div>
    );
}

// ─── Bulk Upload Result Modal ──────────────────────────────────────────────────
function BulkResultModal({ result, onClose }) {
    const { uploaded = [], skipped = [], errors = [], info } = result;
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 700,
        }}>
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: 28, width: 520, maxHeight: '80vh', overflowY: 'auto',
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>📦 Bulk Upload Result</div>
                {info && <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>{info}</div>}

                <Section label={`✅ Uploaded (${uploaded.length})`} color={T.safe} items={uploaded} />
                <Section label={`⏭ Skipped – already in DB (${skipped.length})`} color='#f5a623' items={skipped} />
                {errors.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#e84040', marginBottom: 6 }}>
                            ❌ Errors ({errors.length})
                        </div>
                        {errors.map((e, i) => (
                            <div key={i} style={{ fontSize: 11, color: '#e84040', marginBottom: 3 }}>
                                {e.file}: {typeof e.error === 'string' ? e.error : JSON.stringify(e.error)}
                            </div>
                        ))}
                    </div>
                )}
                <div style={{ marginTop: 20, textAlign: 'right' }}>
                    <Btn onClick={onClose}>Close</Btn>
                </div>
            </div>
        </div>
    );
}

function Section({ label, color, items }) {
    if (!items.length) return null;
    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 6 }}>{label}</div>
            {items.map((it, i) => {
                let display = it;
                if (typeof it === 'object' && it !== null) {
                    // Display object properties: name, designator, file, etc.
                    display = it.name || it.designator || it.file || JSON.stringify(it);
                }
                return (
                    <div key={i} style={{ fontSize: 11, color: T.white, marginBottom: 3, paddingLeft: 10 }}>• {display}</div>
                );
            })}
        </div>
    );
}

// ─── Procedures Table Tab ─────────────────────────────────────────────────────
function ProceduresTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [subTab, setSubTab] = useState('pbn'); // 'pbn' | 'conventional'
    const [showMap, setShowMap] = useState(false);

    const PBN_COLS = [
        { key: 'procedure_name',       label: 'Procedure Name',        w: 260 },
        { key: 'airport_icao',         label: 'Airport',               w: 80  },
        { key: 'runway',               label: 'Runway',                w: 90  },
        { key: 'procedure_type',       label: 'Type',                  w: 110 },
        { key: 'initial_moca_ft',      label: 'Initial MOCA (ft)',     w: 120 },
        { key: 'intermediate_moca_ft', label: 'Interm. MOCA (ft)',     w: 130 },
        { key: 'final_oca_ft',         label: 'Final OCA (ft)',        w: 110 },
        { key: 'taa_si_ft',            label: 'TAA SI (ft)',           w: 80  },
        { key: 'taa_lb_ft',            label: 'TAA LB (ft)',           w: 80  },
        { key: 'taa_rb_ft',            label: 'TAA RB (ft)',           w: 80  },
        { key: 'hold_ft',              label: 'Hold (ft)',             w: 90  },
        { key: 'json_layers',          label: 'JSON',                  w: 56  },
        { key: 'uploaded_at',          label: 'Uploaded',              w: 170 },
    ];
    // Conventional (VOR/NDB) publishes racetrack/procedure-turn minima
    // instead of PBN's TAA sectors — see routes/procedures.py's _HEADER_FIELD_MAP.
    const CONVENTIONAL_COLS = [
        { key: 'procedure_name',          label: 'Procedure Name',        w: 260 },
        { key: 'airport_icao',            label: 'Airport',               w: 80  },
        { key: 'runway',                  label: 'Runway',                w: 90  },
        { key: 'procedure_type',          label: 'Type',                  w: 90  },
        { key: 'initial_moca_ft',         label: 'Initial MOCA (ft)',     w: 120 },
        { key: 'racetrack_moca_ft',       label: 'Racetrack MOCA (ft)',   w: 130 },
        { key: 'procedure_turn_moca_ft',  label: 'Proc Turn MOCA (ft)',   w: 130 },
        { key: 'intermediate_moca_ft',    label: 'Interm. MOCA (ft)',     w: 130 },
        { key: 'final_oca_ft',            label: 'Final OCA (ft)',        w: 110 },
        { key: 'hold_ft',                 label: 'Hold (ft)',             w: 90  },
        { key: 'json_layers',             label: 'JSON',                  w: 56  },
        { key: 'uploaded_at',             label: 'Uploaded',              w: 170 },
    ];
    const PROC_COLS = subTab === 'conventional' ? CONVENTIONAL_COLS : PBN_COLS;
    const MOCA_LIST_COLS = new Set(['initial_moca_ft', 'intermediate_moca_ft', 'final_oca_ft', 'racetrack_moca_ft', 'procedure_turn_moca_ft', 'taa_si_ft', 'taa_lb_ft', 'taa_rb_ft']);

    const [editingTaa, setEditingTaa] = useState(null);  // { id, si, lb, rb }
    const [selectedIds, setSelectedIds] = useState(new Set());

    const visibleRows = rows.filter(r => (subTab === 'conventional' ? r.procedure_category === 'CONVENTIONAL' : r.procedure_category !== 'CONVENTIONAL'));

    const allSelected = visibleRows.length > 0 && visibleRows.every(r => selectedIds.has(r.id));
    const someSelected = visibleRows.some(r => selectedIds.has(r.id)) && !allSelected;

    function toggleAll(checked) {
        setSelectedIds(checked ? new Set(visibleRows.map(r => r.id)) : new Set());
    }
    function toggleRow(id, checked) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getProcedures();
            setRows(data || []);
        } catch (e) {
            console.error('Failed to load procedures', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    // Switching sub-tab can leave stale cross-category selections behind.
    useEffect(() => { setSelectedIds(new Set()); }, [subTab]);

    async function handleDelete(id) {
        if (!window.confirm('Delete this procedure?')) return;
        try {
            await deleteProcedure(id);
            setRows(prev => prev.filter(r => r.id !== id));
            setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleDeleteSelected() {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected procedure${selectedIds.size > 1 ? 's' : ''}?`)) return;
        let failed = 0;
        for (const id of selectedIds) {
            try { await deleteProcedure(id); }
            catch { failed++; }
        }
        await load();
        setSelectedIds(new Set());
        flash(failed ? `✕ ${failed} deletion(s) failed` : `✓ Deleted ${selectedIds.size} procedure(s)`);
    }

    async function handleDeleteAll() {
        if (visibleRows.length === 0) return;
        if (!window.confirm(`Delete ALL ${visibleRows.length} ${subTab === 'conventional' ? 'CONVENTIONAL' : 'PBN'} procedures? This cannot be undone.`)) return;
        let failed = 0;
        for (const row of visibleRows) {
            try { await deleteProcedure(row.id); }
            catch { failed++; }
        }
        await load();
        setSelectedIds(new Set());
        flash(failed ? `✕ ${failed} deletion(s) failed` : '✓ All procedures deleted');
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadProcs();
            setBulkResult(data);
            if (data.uploaded?.length) await load();
        } catch (e) {
            setBulkResult({ uploaded: [], skipped: [], errors: [{ file: 'server', error: e.response?.data?.detail || e.message }] });
        } finally {
            setBulkLoading(false);
        }
    }
    const [reparseLoading, setReparseLoading] = useState(false);

    async function handleReparse() {
        if (!window.confirm('Re-parse json_layers for all procedures that are currently missing it?')) return;
        setReparseLoading(true);
        try {
            const { data } = await reparseAllProcs();
            const n = data.updated?.length || 0;
            const e = data.errors?.length || 0;
            flash(`✓ Updated ${n} procedure${n !== 1 ? 's' : ''}${e ? ` (${e} errors)` : ''}`);
            if (n) await load();
        } catch (e) {
            const msg = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message);
            flash('✕ Re-parse failed: ' + msg);
        } finally {
            setReparseLoading(false);
        }
    }

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    function formatDate(iso) {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString(); } catch { return iso; }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Sub-tabs */}
            <div style={{ display: 'flex', gap: 4, background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 8, padding: 4, width: 'fit-content' }}>
                {[{ key: 'pbn', label: 'PBN' }, { key: 'conventional', label: 'CONVENTIONAL' }].map(s => (
                    <button key={s.key} onClick={() => setSubTab(s.key)} style={{
                        padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: subTab === s.key ? T.blue : 'transparent',
                        color: subTab === s.key ? '#fff' : T.muted,
                        fontSize: 11, fontWeight: 700,
                    }}>{s.label}</button>
                ))}
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Btn variant="success" small onClick={() => setShowAdd(true)}>+ Add Procedure</Btn>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}
                    title="Scans both procedures/PBN/ and procedures/CONVENTIONAL/ regardless of which sub-tab is active">
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                <Btn small onClick={handleReparse} disabled={reparseLoading}
                    title="Re-parse json_layers from stored KMZ blob for any procedure where it is missing">
                    {reparseLoading ? '⏳ Parsing…' : '🔄 Re-parse JSON'}
                </Btn>
                <Btn small onClick={() => setShowMap(true)} disabled={rows.length === 0}>
                    🗺 View on Map
                </Btn>
                {selectedIds.size > 0 && (
                    <Btn small variant="danger" onClick={handleDeleteSelected}
                        style={{ background: 'rgba(232,64,64,0.15)', border: '1px solid #e8404060', color: '#e84040' }}>
                        🗑 Delete Selected ({selectedIds.size})
                    </Btn>
                )}
                <Btn small onClick={handleDeleteAll} disabled={visibleRows.length === 0}
                    style={{ background: 'rgba(232,64,64,0.08)', border: '1px solid #e8404040', color: '#e84040', marginLeft: 'auto' }}
                    title={`Delete all ${subTab === 'conventional' ? 'CONVENTIONAL' : 'PBN'} procedures`}>
                    🗑 Delete All
                </Btn>
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted }}>
                    {visibleRows.length} procedure{visibleRows.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Table */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : visibleRows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>
                        No {subTab === 'conventional' ? 'CONVENTIONAL' : 'PBN'} procedures yet.{' '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={() => setShowAdd(true)}>Add one →</span>
                        {' or '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={handleBulkUpload}>Run Bulk Upload →</span>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 310px)', scrollbarWidth: 'thin', scrollbarColor: `${T.border} ${T.bg0}` }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.bg0 }}>
                            <tr>
                                <th style={{ padding: '8px 10px', width: 36, borderBottom: `1px solid ${T.border}` }}>
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={el => { if (el) el.indeterminate = someSelected; }}
                                        onChange={e => toggleAll(e.target.checked)}
                                        style={{ accentColor: T.blue, cursor: 'pointer' }}
                                        title="Select all"
                                    />
                                </th>
                                {PROC_COLS.map(c => (
                                    <th key={c.key} style={{
                                        padding: '8px 12px', textAlign: 'left',
                                        fontSize: 10, fontWeight: 700, color: T.white,
                                        width: c.w, minWidth: c.w,
                                        borderBottom: `1px solid ${T.border}`,
                                        textTransform: 'uppercase', letterSpacing: '0.05em',
                                    }}>{c.label}</th>
                                ))}
                                <th style={{ padding: '8px 12px', width: 70, borderBottom: `1px solid ${T.border}` }} />
                            </tr>
                        </thead>
                            <tbody>
                                {visibleRows.map((row, ri) => (
                                    <tr key={row.id} style={{
                                        borderBottom: `1px solid ${T.border}22`,
                                        background: selectedIds.has(row.id)
                                            ? 'rgba(232,64,64,0.06)'
                                            : ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)',
                                    }}>
                                        <td style={{ padding: '6px 10px', width: 36 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                onChange={e => toggleRow(row.id, e.target.checked)}
                                                style={{ accentColor: T.blue, cursor: 'pointer' }}
                                            />
                                        </td>
                                        {PROC_COLS.map(c => (
                                            <td key={c.key} style={{
                                                padding: '8px 12px', color: T.white,
                                                width: c.w, minWidth: c.w, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {c.key === 'uploaded_at'
                                                    ? formatDate(row[c.key])
                                                    : c.key === 'json_layers'
                                                        ? (row[c.key]
                                                            ? <span style={{ color: T.safe, fontWeight: 700, fontSize: 13 }}>✓</span>
                                                            : <span style={{ color: '#e84040', fontWeight: 700, fontSize: 13 }}>✗</span>)
                                                        : MOCA_LIST_COLS.has(c.key)
                                                            ? (row[c.key]?.length ? row[c.key].join(';') : <span style={{ color: T.muted }}>—</span>)
                                                            : (row[c.key] ?? <span style={{ color: T.muted }}>—</span>)
                                                }
                                            </td>
                                        ))}
                                        <td style={{ padding: '6px 8px', textAlign: 'center', width: 100 }}>
                                            <button
                                                onClick={() => setEditingTaa({
                                                    id: row.id,
                                                    si: (row.taa_si_ft || []).join(';'),
                                                    lb: (row.taa_lb_ft || []).join(';'),
                                                    rb: (row.taa_rb_ft || []).join(';'),
                                                })}
                                                title="Set TAA sector lower-limit altitudes"
                                                style={{
                                                    background: 'rgba(155,89,182,0.12)', border: '1px solid #9B59B630',
                                                    color: '#9B59B6', borderRadius: 4, padding: '2px 7px',
                                                    fontSize: 10, cursor: 'pointer', marginRight: 4,
                                                }}
                                            >✈ TAA</button>
                                            <button
                                                onClick={() => handleDelete(row.id)}
                                                style={{
                                                    background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030',
                                                    color: '#e84040', borderRadius: 4, padding: '2px 8px',
                                                    fontSize: 11, cursor: 'pointer',
                                                }}
                                            >✕ Del</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* TAA Altitude Edit Modal */}
            {editingTaa && (
                <div className="dg-modal-overlay" style={{ zIndex: 1000 }}>
                    <div className="dg-modal" style={{ padding: 28, width: 340 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#e0e0e0', marginBottom: 18 }}>✈ TAA Sector Lower Limits</div>
                        <div style={{ fontSize: 10, color: '#888', marginBottom: 14, lineHeight: 1.4 }}>
                            A quadrant split into more than one sector (e.g. by distance from the IAF)
                            takes multiple values separated by <code>;</code>, e.g. <code>7300;8200</code>.
                        </div>
                        {[
                            { label: 'Straight-In (ft)', k: 'si' },
                            { label: 'Left Base (ft)',   k: 'lb' },
                            { label: 'Right Base (ft)',  k: 'rb' },
                        ].map(({ label, k }) => (
                            <div key={k} style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 10, color: '#9B59B6', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                                <input type="text" value={editingTaa[k]} onChange={e => setEditingTaa(prev => ({ ...prev, [k]: e.target.value }))}
                                    className="dg-input" style={{ fontSize: 13 }} placeholder="ft MSL, e.g. 7300;8200" />
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                            <button onClick={() => setEditingTaa(null)} style={{ background: 'transparent', border: '1px solid #2e4a6a', color: '#aaa', borderRadius: 6, padding: '6px 16px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={async () => {
                                try {
                                    await patchTaaAltitudes(editingTaa.id, { taa_si_ft: editingTaa.si || null, taa_lb_ft: editingTaa.lb || null, taa_rb_ft: editingTaa.rb || null });
                                    setEditingTaa(null); load();
                                } catch (e) { alert('Save failed: ' + e.message); }
                            }} style={{ background: '#9B59B6', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 20px', cursor: 'pointer', fontWeight: 700 }}>Save</button>
                        </div>
                    </div>
                </div>
            )}
            {showAdd && <AddProcedureModal onClose={() => setShowAdd(false)} onAdded={row => { setRows(p => [row, ...p]); flash('✓ Uploaded'); }} />}
            {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
            {showMap && <ProceduresMapModal procedures={rows} onClose={() => setShowMap(false)} />}
        </div>
    );
}

// ─── Procedures: View on Map — pick an aerodrome, toggle which of its ────────
// procedures' KMZ geometry (Polygon/LineString/Point) draws on the map.
// Deliberately generic (not segment/MOC-aware like NpaPage.jsx's collect*
// functions) — this is a visual reference tool, not an assessment engine, so
// it just walks the whole json_layers tree and draws everything it finds,
// colored per-procedure so multiple selections stay distinguishable.
const PROC_MAP_COLORS = ['#3a8ac4', '#e84040', '#2ec98b', '#f5a623', '#9B59B6', '#16A085', '#E91E63', '#FFB020'];

function ProceduresMapModal({ procedures, onClose }) {
    const mapRef = useRef(null), mapInst = useRef(null), tileRef = useRef(null);
    const layersByProcRef = useRef({}); // procId -> L.FeatureGroup
    const [mapMode, setMapMode] = useState('street');
    const [selectedIcao, setSelectedIcao] = useState(null);
    const [visibleProcIds, setVisibleProcIds] = useState(new Set());
    const [loadingIds, setLoadingIds] = useState(new Set());
    const [layersCache, setLayersCache] = useState({}); // procId -> json_layers tree

    const byIcao = useMemo(() => {
        const m = {};
        (procedures || []).forEach(p => {
            const k = p.airport_icao || '—';
            (m[k] = m[k] || []).push(p);
        });
        return m;
    }, [procedures]);
    const icaos = useMemo(() => Object.keys(byIcao).sort(), [byIcao]);

    useEffect(() => {
        if (!icaos.length) return;
        setSelectedIcao(prev => (prev && byIcao[prev]) ? prev : icaos[0]);
    }, [icaos]); // eslint-disable-line

    // Map init
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6 });
        L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        tileRef.current = null;
        setTimeout(() => mapInst.current?.invalidateSize(), 0);
        return () => { mapInst.current?.remove(); mapInst.current = null; };
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const l = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // Switching aerodrome drops whatever was drawn/selected for the previous one.
    useEffect(() => {
        setVisibleProcIds(new Set());
        return () => {
            Object.values(layersByProcRef.current).forEach(g => { try { mapInst.current?.removeLayer(g); } catch { /* noop */ } });
            layersByProcRef.current = {};
        };
    }, [selectedIcao]);

    function colorForIndex(i) { return PROC_MAP_COLORS[i % PROC_MAP_COLORS.length]; }

    function drawProcedure(proc, tree, color) {
        if (!mapInst.current) return;
        const group = L.featureGroup();
        const walk = (node) => {
            (node.features || []).forEach(f => {
                if (!f.coords) return;
                const tip = `<b>${proc.procedure_name}</b><br/>${f.name || node.name || ''}`;
                if (f.geom === 'Polygon') {
                    const ring = f.coords[0] || f.coords;
                    L.polygon(ring.map(([lat, lon]) => [lat, lon]), { color, weight: 1, fillColor: color, fillOpacity: 0.15 })
                        .bindTooltip(tip).addTo(group);
                } else if (f.geom === 'LineString') {
                    L.polyline(f.coords.map(([lat, lon]) => [lat, lon]), { color, weight: 2 })
                        .bindTooltip(tip).addTo(group);
                } else if (f.geom === 'Point') {
                    const [lat, lon] = f.coords;
                    L.circleMarker([lat, lon], { radius: 3, color, fillColor: color, fillOpacity: 1 })
                        .bindTooltip(tip).addTo(group);
                }
            });
            (node.children || []).forEach(walk);
        };
        walk(tree || {});
        group.addTo(mapInst.current);
        layersByProcRef.current[proc.id] = group;
        if (group.getLayers().length > 0) {
            mapInst.current.fitBounds(group.getBounds().pad(0.1));
        }
    }

    async function toggleProc(proc, idx) {
        const id = proc.id;
        if (visibleProcIds.has(id)) {
            setVisibleProcIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            const layer = layersByProcRef.current[id];
            if (layer && mapInst.current) mapInst.current.removeLayer(layer);
            delete layersByProcRef.current[id];
            return;
        }
        setVisibleProcIds(prev => new Set(prev).add(id));
        let tree = layersCache[id];
        if (!tree) {
            setLoadingIds(prev => new Set(prev).add(id));
            try {
                const { data } = await getProcLayers(id);
                tree = data;
                setLayersCache(prev => ({ ...prev, [id]: tree }));
            } catch (e) {
                console.error('Failed to load procedure layers', e);
                setVisibleProcIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                setLoadingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                return;
            }
            setLoadingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
        }
        drawProcedure(proc, tree, colorForIndex(idx));
    }

    const currentProcs = selectedIcao ? (byIcao[selectedIcao] || []) : [];

    return (
        <div className="dg-modal-overlay" style={{ zIndex: 900 }}>
            <div className="dg-modal" style={{ width: '92vw', height: '88vh', padding: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: T.white }}>🗺 Procedures — View on Map</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 20, cursor: 'pointer' }}>✕</button>
                </div>
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    <div style={{ width: 280, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: 12, borderBottom: `1px solid ${T.border}` }}>
                            <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 6 }}>Aerodrome</div>
                            <select value={selectedIcao || ''} onChange={e => setSelectedIcao(e.target.value)}
                                style={{ width: '100%', background: T.bg0, border: `1px solid ${T.border}`, color: T.white, padding: '6px 8px', borderRadius: 4, fontSize: 13 }}>
                                {icaos.map(icao => (
                                    <option key={icao} value={icao}>{icao} ({byIcao[icao].length})</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                            {currentProcs.length === 0 ? (
                                <div style={{ padding: 16, color: T.muted, fontSize: 12, textAlign: 'center' }}>No procedures for this aerodrome.</div>
                            ) : currentProcs.map((p, i) => {
                                const on = visibleProcIds.has(p.id);
                                const isLoading = loadingIds.has(p.id);
                                return (
                                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, background: on ? 'rgba(255,255,255,0.04)' : 'transparent' }}>
                                        <input type="checkbox" checked={on} onChange={() => toggleProc(p, i)} style={{ accentColor: colorForIndex(i), cursor: 'pointer' }} />
                                        <span style={{ width: 10, height: 10, borderRadius: 3, background: colorForIndex(i), flexShrink: 0 }} />
                                        <span style={{ fontSize: 12, color: T.white, flex: 1 }}>{p.procedure_name}</span>
                                        {isLoading && <span style={{ fontSize: 10, color: T.muted }}>⏳</span>}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}` }}>
                            {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                                <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? '#fff' : T.muted, fontSize: 11 }}>{layer.label}</button>
                            ))}
                        </div>
                        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── MSA: Confirmed (real, polygon-backed) vs Generic (flat 30NM/lowest value) ─
function MsaTab() {
    const [subTab, setSubTab] = useState('confirmed'); // 'confirmed' | 'generic'
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 4, background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 8, padding: 4, width: 'fit-content' }}>
                {[{ key: 'confirmed', label: 'Confirmed MSA' }, { key: 'generic', label: 'Generic MSA' }].map(s => (
                    <button key={s.key} onClick={() => setSubTab(s.key)} style={{
                        padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: subTab === s.key ? T.blue : 'transparent',
                        color: subTab === s.key ? '#fff' : T.muted,
                        fontSize: 11, fontWeight: 700,
                    }}>{s.label}</button>
                ))}
            </div>
            {subTab === 'confirmed' ? <ConfirmedMsaTab /> : <TableView tab="msa" />}
        </div>
    );
}

function ConfirmedMsaTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getMsaSectors();
            setRows(data || []);
        } catch (e) {
            console.error('Failed to load confirmed MSA sectors', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    function formatDate(iso) {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString(); } catch { return iso; }
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadMsaSectors();
            setBulkResult(data);
            if (data.uploaded?.length) await load();
        } catch (e) {
            setBulkResult({ uploaded: [], errors: [{ file: 'server', error: e.response?.data?.detail || e.message }] });
        } finally {
            setBulkLoading(false);
        }
    }

    async function handleDelete(ident) {
        if (!window.confirm(`Delete confirmed MSA data for navaid '${ident}'? Aerodromes resolving to it will revert to the generic 30NM fallback.`)) return;
        try {
            await deleteMsaSectors(ident);
            setRows(prev => prev.filter(r => r.navaid_ident !== ident));
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.5 }}>
                Real, sector-based MSA loaded from <code>backend/MSA/*.kmz</code> + its minima spreadsheet — keyed by
                navaid ident (e.g. <code>BLV</code>). When an aerodrome's resolved VOR/NDB has a row here, the MSA tab
                uses these sector polygons + per-sector altitudes instead of the generic 30NM-circle fallback.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}>
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted }}>
                    {rows.length} navaid{rows.length !== 1 ? 's' : ''} confirmed
                </span>
            </div>

            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>
                        No confirmed MSA data yet.{' '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={handleBulkUpload}>Run Bulk Upload →</span>
                    </div>
                ) : (
                    <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                        <thead>
                            <tr>
                                {['Navaid', 'Sectors', 'Sector Altitudes (ft)', 'Uploaded', ''].map(h => (
                                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, borderBottom: `1px solid ${T.border}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, ri) => (
                                <tr key={row.navaid_ident} style={{ borderBottom: `1px solid ${T.border}22`, background: ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)' }}>
                                    <td style={{ padding: '8px 12px', color: T.white, fontWeight: 700 }}>{row.navaid_ident}</td>
                                    <td style={{ padding: '8px 12px', color: T.white }}>{row.sector_count}</td>
                                    <td style={{ padding: '8px 12px', color: T.white }}>
                                        {(row.sector_alts_ft || []).map((v, i) => v != null ? `S${i + 1}: ${v}` : null).filter(Boolean).join('  ·  ') || <span style={{ color: T.muted }}>—</span>}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: T.white }}>{formatDate(row.uploaded_at)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                        <button onClick={() => handleDelete(row.navaid_ident)} style={{ background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030', color: '#e84040', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>✕ Del</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
        </div>
    );
}

// ─── Add SID Departure Upload Modal ───────────────────────────────────────────
function AddDepartureModal({ onClose, onAdded }) {
    const [form, setForm] = useState({ procedure_name: '', airport_icao: '', runway: '' });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    function handleFileChange(e) {
        const f = e.target.files[0];
        if (!f) return;
        setFile(f);
        if (!form.procedure_name) {
            set('procedure_name', f.name.replace(/\.kmz$/i, ''));
        }
    }

    async function submit() {
        if (!file) { setErr('Please select a .kmz file'); return; }
        setLoading(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
            const { data } = await uploadDeparture(fd);
            if (data.error) { setErr(data.error); return; }
            onAdded(data);
            onClose();
        } catch (e) {
            setErr(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600,
        }}>
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: 28, width: 480, maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>↗️ Add SID Departure</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>KMZ File *</div>
                        <input type="file" accept=".kmz" onChange={handleFileChange}
                            style={{ ...inputStyle, padding: '5px 10px', cursor: 'pointer' }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Procedure Name</div>
                        <input type="text" value={form.procedure_name} onChange={e => set('procedure_name', e.target.value)}
                            style={inputStyle} placeholder="e.g. FALE GREYTOWN 1A RWY 06 DEP (auto-detected if left blank)" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Airport ICAO</div>
                        <input type="text" value={form.airport_icao} onChange={e => set('airport_icao', e.target.value.toUpperCase().slice(0, 4))}
                            style={inputStyle} maxLength={4} placeholder="FALE" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Runway</div>
                        <input type="text" value={form.runway} onChange={e => set('runway', e.target.value)}
                            style={inputStyle} placeholder="06" />
                    </div>
                </div>
                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>
                        {loading ? <span>⏳ Uploading…</span> : '⬆ Upload'}
                    </Btn>
                </div>
            </div>
        </div>
    );
}

// ─── SID Departures Table Tab ──────────────────────────────────────────────────
function DeparturesTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [reparseLoading, setReparseLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const DEP_COLS = [
        { key: 'procedure_name', label: 'Procedure Name', w: 280 },
        { key: 'airport_icao',   label: 'Airport',        w: 80  },
        { key: 'runway',         label: 'Runway',         w: 90  },
        { key: 'json_features',  label: 'JSON',           w: 56  },
        { key: 'uploaded_at',    label: 'Uploaded',       w: 170 },
    ];

    const allSelected = rows.length > 0 && selectedIds.size === rows.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    function toggleAll(checked) {
        setSelectedIds(checked ? new Set(rows.map(r => r.id)) : new Set());
    }
    function toggleRow(id, checked) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getDepartures();
            setRows(data || []);
        } catch (e) {
            console.error('Failed to load departures', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleDelete(id) {
        if (!window.confirm('Delete this departure procedure?')) return;
        try {
            await deleteDeparture(id);
            setRows(prev => prev.filter(r => r.id !== id));
            setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleDeleteSelected() {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected departure(s)?`)) return;
        let failed = 0;
        for (const id of selectedIds) {
            try { await deleteDeparture(id); }
            catch { failed++; }
        }
        await load();
        setSelectedIds(new Set());
        flash(failed ? `✕ ${failed} deletion(s) failed` : `✓ Deleted ${selectedIds.size} departure(s)`);
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadDepartures();
            setBulkResult(data);
            if (data.uploaded?.length) await load();
        } catch (e) {
            setBulkResult({ uploaded: [], skipped: [], errors: [{ file: 'server', error: e.response?.data?.detail || e.message }] });
        } finally {
            setBulkLoading(false);
        }
    }

    async function handleReparse() {
        if (!window.confirm('Re-parse json_features for all SID departures from their stored KMZ?')) return;
        setReparseLoading(true);
        try {
            const { data } = await reparseAllDepartures();
            const n = data.updated?.length || 0;
            const e = data.errors?.length || 0;
            flash(`✓ Updated ${n} departure${n !== 1 ? 's' : ''}${e ? ` (${e} errors)` : ''}`);
            if (n) await load();
        } catch (e) {
            const msg = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message);
            flash('✕ Re-parse failed: ' + msg);
        } finally {
            setReparseLoading(false);
        }
    }

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    function formatDate(iso) {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString(); } catch { return iso; }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Btn variant="success" small onClick={() => setShowAdd(true)}>+ Add Departure</Btn>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}>
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                <Btn small onClick={handleReparse} disabled={reparseLoading}
                    title="Re-parse json_features from stored KMZ blob for every SID departure">
                    {reparseLoading ? '⏳ Parsing…' : '🔄 Reparse JSON'}
                </Btn>
                {selectedIds.size > 0 && (
                    <Btn small variant="danger" onClick={handleDeleteSelected}
                        style={{ background: 'rgba(232,64,64,0.15)', border: '1px solid #e8404060', color: '#e84040' }}>
                        🗑 Delete Selected ({selectedIds.size})
                    </Btn>
                )}
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>
                    {rows.length} departure(s)
                </span>
            </div>

            {/* Table */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>
                        No SID departures yet.{' '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={() => setShowAdd(true)}>Add one →</span>
                        {' or '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={handleBulkUpload}>Run Bulk Upload →</span>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 310px)', scrollbarWidth: 'thin', scrollbarColor: `${T.border} ${T.bg0}` }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.bg0 }}>
                                <tr>
                                    <th style={{ padding: '8px 10px', width: 36, borderBottom: `1px solid ${T.border}` }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected; }}
                                            onChange={e => toggleAll(e.target.checked)}
                                            style={{ accentColor: T.blue, cursor: 'pointer' }}
                                            title="Select all"
                                        />
                                    </th>
                                    {DEP_COLS.map(c => (
                                        <th key={c.key} style={{
                                            padding: '8px 12px', textAlign: 'left',
                                            fontSize: 10, fontWeight: 700, color: T.white,
                                            width: c.w, minWidth: c.w,
                                            borderBottom: `1px solid ${T.border}`,
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                        }}>{c.label}</th>
                                    ))}
                                    <th style={{ padding: '8px 12px', width: 70, borderBottom: `1px solid ${T.border}` }} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={row.id} style={{
                                        borderBottom: `1px solid ${T.border}22`,
                                        background: selectedIds.has(row.id)
                                            ? 'rgba(232,64,64,0.06)'
                                            : ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)',
                                    }}>
                                        <td style={{ padding: '6px 10px', width: 36 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                onChange={e => toggleRow(row.id, e.target.checked)}
                                                style={{ accentColor: T.blue, cursor: 'pointer' }}
                                            />
                                        </td>
                                        {DEP_COLS.map(c => (
                                            <td key={c.key} style={{
                                                padding: '8px 12px', color: T.white,
                                                width: c.w, minWidth: c.w, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {c.key === 'uploaded_at'
                                                    ? formatDate(row[c.key])
                                                    : c.key === 'json_features'
                                                        ? (row[c.key]
                                                            ? <span style={{ color: T.safe, fontWeight: 700, fontSize: 13 }}>✓</span>
                                                            : <span style={{ color: '#e84040', fontWeight: 700, fontSize: 13 }}>✗</span>)
                                                        : (row[c.key] ?? <span style={{ color: T.muted }}>—</span>)
                                                }
                                            </td>
                                        ))}
                                        <td style={{ padding: '6px 8px', textAlign: 'center', width: 70 }}>
                                            <button
                                                onClick={() => handleDelete(row.id)}
                                                style={{
                                                    background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030',
                                                    color: '#e84040', borderRadius: 4, padding: '2px 8px',
                                                    fontSize: 11, cursor: 'pointer',
                                                }}
                                            >✕ Del</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showAdd && <AddDepartureModal onClose={() => setShowAdd(false)} onAdded={row => { setRows(p => [row, ...p]); flash('✓ Uploaded'); }} />}
            {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
        </div>
    );
}

// ─── Add STAR Arrival Upload Modal ──────────────────────────────────────────────
function AddArrivalModal({ onClose, onAdded }) {
    const [form, setForm] = useState({ procedure_name: '', airport_icao: '', runway: '', minima_ft: '' });
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    function handleFileChange(e) {
        const f = e.target.files[0];
        if (!f) return;
        setFile(f);
        if (!form.procedure_name) {
            set('procedure_name', f.name.replace(/\.kmz$/i, ''));
        }
    }

    async function submit() {
        if (!file) { setErr('Please select a .kmz file'); return; }
        setLoading(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
            const { data } = await uploadArrival(fd);
            if (data.error) { setErr(data.error); return; }
            onAdded(data);
            onClose();
        } catch (e) {
            setErr(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600,
        }}>
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: 28, width: 480, maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>↘️ Add STAR Arrival</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>KMZ File *</div>
                        <input type="file" accept=".kmz" onChange={handleFileChange}
                            style={{ ...inputStyle, padding: '5px 10px', cursor: 'pointer' }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Procedure Name</div>
                        <input type="text" value={form.procedure_name} onChange={e => set('procedure_name', e.target.value)}
                            style={inputStyle} placeholder="e.g. FALE DUNSA 1A ARRIVAL RWY 06 (auto-detected if left blank)" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Airport ICAO</div>
                        <input type="text" value={form.airport_icao} onChange={e => set('airport_icao', e.target.value.toUpperCase().slice(0, 4))}
                            style={inputStyle} maxLength={4} placeholder="FALE" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Runway</div>
                        <input type="text" value={form.runway} onChange={e => set('runway', e.target.value)}
                            style={inputStyle} placeholder="06" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Segment Minima (ft, comma-separated, in segment order)</div>
                        <input type="text" value={form.minima_ft} onChange={e => set('minima_ft', e.target.value)}
                            style={inputStyle} placeholder="e.g. 5100,5100,4000 (auto-matched from STAR MINIMA.xlsx if left blank)" />
                    </div>
                </div>
                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>
                        {loading ? <span>⏳ Uploading…</span> : '⬆ Upload'}
                    </Btn>
                </div>
            </div>
        </div>
    );
}

// ─── STAR Arrivals Table Tab ─────────────────────────────────────────────────────
function ArrivalsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [reparseLoading, setReparseLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const ARR_COLS = [
        { key: 'procedure_name', label: 'Procedure Name', w: 280 },
        { key: 'airport_icao',   label: 'Airport',        w: 80  },
        { key: 'runway',         label: 'Runway',         w: 90  },
        { key: 'minima_ft',      label: 'Minima (ft)',    w: 160 },
        { key: 'json_features',  label: 'JSON',           w: 56  },
        { key: 'uploaded_at',    label: 'Uploaded',       w: 170 },
    ];

    const allSelected = rows.length > 0 && selectedIds.size === rows.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    function toggleAll(checked) {
        setSelectedIds(checked ? new Set(rows.map(r => r.id)) : new Set());
    }
    function toggleRow(id, checked) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getArrivals();
            setRows(data || []);
        } catch (e) {
            console.error('Failed to load arrivals', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleDelete(id) {
        if (!window.confirm('Delete this arrival procedure?')) return;
        try {
            await deleteArrival(id);
            setRows(prev => prev.filter(r => r.id !== id));
            setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleDeleteSelected() {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected arrival(s)?`)) return;
        let failed = 0;
        for (const id of selectedIds) {
            try { await deleteArrival(id); }
            catch { failed++; }
        }
        await load();
        setSelectedIds(new Set());
        flash(failed ? `✕ ${failed} deletion(s) failed` : `✓ Deleted ${selectedIds.size} arrival(s)`);
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadArrivals();
            setBulkResult(data);
            if (data.uploaded?.length) await load();
        } catch (e) {
            setBulkResult({ uploaded: [], skipped: [], errors: [{ file: 'server', error: e.response?.data?.detail || e.message }] });
        } finally {
            setBulkLoading(false);
        }
    }

    async function handleReparse() {
        if (!window.confirm('Re-parse json_features and re-match minima for all STAR arrivals from their stored KMZ?')) return;
        setReparseLoading(true);
        try {
            const { data } = await reparseAllArrivals();
            const n = data.updated?.length || 0;
            const e = data.errors?.length || 0;
            flash(`✓ Updated ${n} arrival${n !== 1 ? 's' : ''}${e ? ` (${e} errors)` : ''}`);
            if (n) await load();
        } catch (e) {
            const msg = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message);
            flash('✕ Re-parse failed: ' + msg);
        } finally {
            setReparseLoading(false);
        }
    }

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    function formatDate(iso) {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString(); } catch { return iso; }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Btn variant="success" small onClick={() => setShowAdd(true)}>+ Add Arrival</Btn>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}>
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                <Btn small onClick={handleReparse} disabled={reparseLoading}
                    title="Re-parse json_features from stored KMZ blob and re-match minima for every STAR arrival">
                    {reparseLoading ? '⏳ Parsing…' : '🔄 Reparse JSON'}
                </Btn>
                {selectedIds.size > 0 && (
                    <Btn small variant="danger" onClick={handleDeleteSelected}
                        style={{ background: 'rgba(232,64,64,0.15)', border: '1px solid #e8404060', color: '#e84040' }}>
                        🗑 Delete Selected ({selectedIds.size})
                    </Btn>
                )}
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>
                    {rows.length} arrival(s)
                </span>
            </div>

            {/* Table */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>
                        No STAR arrivals yet.{' '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={() => setShowAdd(true)}>Add one →</span>
                        {' or '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={handleBulkUpload}>Run Bulk Upload →</span>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 310px)', scrollbarWidth: 'thin', scrollbarColor: `${T.border} ${T.bg0}` }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.bg0 }}>
                                <tr>
                                    <th style={{ padding: '8px 10px', width: 36, borderBottom: `1px solid ${T.border}` }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected; }}
                                            onChange={e => toggleAll(e.target.checked)}
                                            style={{ accentColor: T.blue, cursor: 'pointer' }}
                                            title="Select all"
                                        />
                                    </th>
                                    {ARR_COLS.map(c => (
                                        <th key={c.key} style={{
                                            padding: '8px 12px', textAlign: 'left',
                                            fontSize: 10, fontWeight: 700, color: T.white,
                                            width: c.w, minWidth: c.w,
                                            borderBottom: `1px solid ${T.border}`,
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                        }}>{c.label}</th>
                                    ))}
                                    <th style={{ padding: '8px 12px', width: 70, borderBottom: `1px solid ${T.border}` }} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={row.id} style={{
                                        borderBottom: `1px solid ${T.border}22`,
                                        background: selectedIds.has(row.id)
                                            ? 'rgba(232,64,64,0.06)'
                                            : ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)',
                                    }}>
                                        <td style={{ padding: '6px 10px', width: 36 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                onChange={e => toggleRow(row.id, e.target.checked)}
                                                style={{ accentColor: T.blue, cursor: 'pointer' }}
                                            />
                                        </td>
                                        {ARR_COLS.map(c => (
                                            <td key={c.key} style={{
                                                padding: '8px 12px', color: T.white,
                                                width: c.w, minWidth: c.w, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {c.key === 'uploaded_at'
                                                    ? formatDate(row[c.key])
                                                    : c.key === 'json_features'
                                                        ? (row[c.key]
                                                            ? <span style={{ color: T.safe, fontWeight: 700, fontSize: 13 }}>✓</span>
                                                            : <span style={{ color: '#e84040', fontWeight: 700, fontSize: 13 }}>✗</span>)
                                                        : c.key === 'minima_ft'
                                                            ? (row[c.key]?.length ? row[c.key].map(v => v).join(' · ') : <span style={{ color: T.muted }}>—</span>)
                                                            : (row[c.key] ?? <span style={{ color: T.muted }}>—</span>)
                                                }
                                            </td>
                                        ))}
                                        <td style={{ padding: '6px 8px', textAlign: 'center', width: 70 }}>
                                            <button
                                                onClick={() => handleDelete(row.id)}
                                                style={{
                                                    background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030',
                                                    color: '#e84040', borderRadius: 4, padding: '2px 8px',
                                                    fontSize: 11, cursor: 'pointer',
                                                }}
                                            >✕ Del</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showAdd && <AddArrivalModal onClose={() => setShowAdd(false)} onAdded={row => { setRows(p => [row, ...p]); flash('✓ Uploaded'); }} />}
            {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
        </div>
    );
}

// ─── Add RMAC Upload Modal ─────────────────────────────────────────────────────
function AddRmacModal({ onClose, onAdded }) {
    const [form, setForm] = useState({ aerodrome: '', chart_name: '' });
    const [file, setFile] = useState(null);
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    async function handleFileChange(e) {
        const f = e.target.files[0];
        if (!f) return;
        setFile(f);
        if (!form.aerodrome) {
            const icao = f.name.split(' ')[0].toUpperCase();
            if (icao.length === 4) set('aerodrome', icao);
        }
        if (!form.chart_name) {
            set('chart_name', f.name.replace(/\.kmz$/i, ''));
        }
    }

    async function submit() {
        if (!file) { setErr('Please select a .kmz file'); return; }
        if (!form.aerodrome.trim() || form.aerodrome.length !== 4) { setErr('Aerodrome must be a 4-letter ICAO code'); return; }
        setLoading(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('aerodrome', form.aerodrome);
            fd.append('chart_name', form.chart_name);
            if (image) fd.append('image', image);
            const { data } = await uploadRmac(fd);
            if (data.error) { setErr(data.error); return; }
            onAdded(data);
            onClose();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600 }}>
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 28, width: 500 }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>📡 Upload RMAC KMZ</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 3 }}>KMZ File *</div>
                        <input type="file" accept=".kmz" onChange={handleFileChange} style={{ ...inputStyle, cursor: 'pointer' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 3 }}>Aerodrome ICAO *</div>
                        <input type="text" value={form.aerodrome} onChange={e => set('aerodrome', e.target.value.toUpperCase().slice(0, 4))} style={inputStyle} maxLength={4} placeholder="e.g. FALE" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 3 }}>Chart Name</div>
                        <input type="text" value={form.chart_name} onChange={e => set('chart_name', e.target.value)} style={inputStyle} placeholder="e.g. FALE RMAC" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 3 }}>Chart Image (PNG) (Optional)</div>
                        <input type="file" accept="image/png" onChange={e => setImage(e.target.files[0])} style={{ ...inputStyle, cursor: 'pointer' }} />
                    </div>
                </div>
                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>{loading ? 'Uploading…' : 'Upload'}</Btn>
                </div>
            </div>
        </div>
    );
}

// ─── OLS Parameters Tab (Annex 14 Table 4-1 / 4-2 quick reference) ────────────
// Read-only — pivots the raw ols_parameters rows (what every OLS surface
// calculation actually reads from) into the same layout as the ICAO document,
// so a wrong value here is visible at a glance instead of only surfacing as
// a wrong assessment result somewhere else.

function pct(v) {
    if (v == null) return '—';
    const s = (v * 100).toFixed(2).replace(/\.?0+$/, '');
    return `${s}%`;
}
function num(v) {
    if (v == null) return '—';
    return Number(v).toLocaleString();
}

const T41_COLS = [
    { label: 'Non-Instrument', sub: 'Code 1', cls: 'Non-Instrument', code: 1 },
    { label: 'Non-Instrument', sub: 'Code 2', cls: 'Non-Instrument', code: 2 },
    { label: 'Non-Instrument', sub: 'Code 3', cls: 'Non-Instrument', code: 3 },
    { label: 'Non-Instrument', sub: 'Code 4', cls: 'Non-Instrument', code: 4 },
    { label: 'Non-Precision',  sub: 'Code 1/2', cls: 'Non-Precision Approach', code: 1 },
    { label: 'Non-Precision',  sub: 'Code 3', cls: 'Non-Precision Approach', code: 3 },
    { label: 'Non-Precision',  sub: 'Code 4', cls: 'Non-Precision Approach', code: 4 },
    { label: 'Precision CAT I', sub: 'Code 1/2', cls: 'Precision Cat I', code: 1 },
    { label: 'Precision CAT I', sub: 'Code 3/4', cls: 'Precision Cat I', code: 3 },
    { label: 'Precision CAT II/III', sub: 'Code 3/4', cls: 'Precision Cat II/III', code: 3 },
];

const T42_COLS = [
    { label: 'Code Number 1', cls: 'Non-Instrument', code: 1 },
    { label: 'Code Number 2', cls: 'Non-Instrument', code: 2 },
    { label: 'Code Number 3 or 4', cls: 'Non-Instrument', code: 3 },
];

function buildParamMap(rows) {
    const map = {};
    rows.forEach(r => { map[`${r.classification}|${r.code}|${r.surface_type}`] = r.parameters; });
    return map;
}

function T41Table({ map }) {
    const p = (col, stype) => map[`${col.cls}|${col.code}|${stype}`] || null;
    const appSections = col => (p(col, 'approach')?.sections) || [];
    const totalLen = col => {
        const secs = appSections(col);
        return secs.length ? secs.reduce((s, [len]) => s + len, 0) : null;
    };

    const groups = [
        { title: 'Conical', rows: [
            { label: 'Conical Slope', cell: () => '5%' },
            { label: 'Conical Height (m)', cell: col => num(p(col, 'conical')?.height) },
        ]},
        { title: 'Inner Horizontal', rows: [
            { label: 'Inner Horizontal Height (m)', cell: () => '45' },
            { label: 'Inner Horizontal Radius (m)', cell: col => num(p(col, 'inner_horizontal')?.radius) },
        ]},
        { title: 'Inner Approach (OFZ — precision only)', rows: [
            { label: 'Inner Approach Width (m)', cell: col => num(p(col, 'inner_approach')?.width),
              notes: col => (col.code === 3 && (col.cls === 'Precision Cat I' || col.cls === 'Precision Cat II/III')) ? ['e'] : null },
            { label: 'Inner Approach Distance from Threshold (m)', cell: col => num(p(col, 'inner_approach')?.distance_thr) },
            { label: 'Inner Approach Length (m)', cell: col => num(p(col, 'inner_approach')?.length),
              notes: col => p(col, 'inner_approach')?.length != null ? ['b'] : null },
            { label: 'Inner Approach Slope', cell: col => pct(p(col, 'inner_approach')?.slope) },
        ]},
        { title: 'Approach', rows: [
            { label: 'Approach Inner Edge (m)', cell: col => num(p(col, 'approach')?.inner_w) },
            { label: 'Approach Distance from Threshold (m)', cell: col => num(p(col, 'approach')?.dist_thr) },
            { label: 'Approach Divergence (each side)', cell: col => pct(p(col, 'approach')?.div) },
            { label: 'First Section Length (m)', cell: col => num(appSections(col)[0]?.[0]) },
            { label: 'First Section Slope', cell: col => pct(appSections(col)[0]?.[1]) },
            { label: 'Second Section Length (m)', cell: col => num(appSections(col)[1]?.[0]) },
            { label: 'Second Section Slope', cell: col => pct(appSections(col)[1]?.[1]) },
            { label: 'Horizontal Section Length (m)', cell: col => num(appSections(col)[2]?.[0]) },
            { label: 'Total Approach Length (m)', cell: col => num(totalLen(col)) },
        ]},
        { title: 'Transitional', rows: [
            { label: 'Transitional Slope', cell: col => pct(p(col, 'transitional')?.trans_slope) },
            { label: 'Inner Transitional Slope', cell: col => pct(p(col, 'inner_transitional')?.slope) },
        ]},
        { title: 'Balked Landing (precision only)', rows: [
            { label: 'Balked Landing Inner Edge (m)', cell: col => num(p(col, 'balked_landing')?.width),
              notes: col => (col.code === 3 && (col.cls === 'Precision Cat I' || col.cls === 'Precision Cat II/III')) ? ['e'] : null },
            { label: 'Balked Landing Distance from Threshold (m)', cell: col => num(p(col, 'balked_landing')?.distance_thr),
              notes: col => {
                  const bl = p(col, 'balked_landing');
                  if (!bl) return null;
                  return bl.distance_thr != null ? ['d'] : ['c'];
              } },
            { label: 'Balked Landing Divergence (each side)', cell: col => pct(p(col, 'balked_landing')?.divergence) },
            { label: 'Balked Landing Slope', cell: col => pct(p(col, 'balked_landing')?.slope) },
        ]},
    ];

    return (
        <>
            <RefTable cols={T41_COLS} groups={groups} noteText={T41_NOTE_TEXT} />
            <Footnotes items={[
                'a. All dimensions are measured horizontally unless specified otherwise.',
                'b. Variable length (see 4.2.9 or 4.2.17).',
                'c. Distance to the end of strip.',
                'd. Or end of runway, whichever is less.',
                'e. Where the code letter is F, width increases to 140 m except for aerodromes accommodating a code letter F aeroplane equipped with digital avionics providing steering commands during the go-around.',
                'Note: See Circulars 301 and 345 (forthcoming) and Chapter 4 of PANS-Aerodromes, Part I (Doc 9981) for further information.',
            ]} />
        </>
    );
}

const T41_NOTE_TEXT = {
    a: 'All dimensions are measured horizontally unless specified otherwise.',
    b: 'Variable length (see 4.2.9 or 4.2.17).',
    c: 'Distance to the end of strip.',
    d: 'Or end of runway, whichever is less.',
    e: 'Where the code letter is F, width increases to 140 m except for aerodromes accommodating a code letter F aeroplane equipped with digital avionics providing steering commands during the go-around.',
};

const T42_NOTE_TEXT = {
    a: 'All dimensions are measured horizontally unless specified otherwise.',
    b: 'The take-off climb surface starts at the end of the clearway if the clearway length exceeds the specified distance.',
    c: '1 800 m when the intended track includes changes of heading greater than 15° for operations conducted in IMC, VMC by night.',
    d: 'See 4.2.24 and 4.2.26.',
};

function T42Table({ map }) {
    const p = col => map[`${col.cls}|${col.code}|take-off`] || null;
    const groups = [
        { title: null, rows: [
            { label: 'Length of Inner Edge (m)', cell: col => num(p(col)?.inner_w) },
            { label: 'Distance from Runway End (m)', cell: col => num(p(col)?.dist_end) },
            { label: 'Divergence (each side)', cell: col => pct(p(col)?.div) },
            { label: 'Final Width (m)', cell: col => num(p(col)?.final_w), notes: col => col.code === 3 ? ['c'] : null },
            { label: 'Length (m)', cell: col => num(p(col)?.length) },
            { label: 'Slope', cell: col => pct(p(col)?.slope), notes: col => col.code === 3 ? ['d'] : null },
        ]},
    ];
    return (
        <>
            <RefTable cols={T42_COLS} groups={groups} noteText={T42_NOTE_TEXT} />
            <Footnotes items={[
                'a. All dimensions are measured horizontally unless specified otherwise.',
                'b. The take-off climb surface starts at the end of the clearway if the clearway length exceeds the specified distance.',
                'c. 1 800 m when the intended track includes changes of heading greater than 15° for operations conducted in IMC, VMC by night.',
                'd. See 4.2.24 and 4.2.26.',
            ]} />
        </>
    );
}

function Footnotes({ items }) {
    return (
        <div style={{ marginTop: 14, fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
            {items.map((t, i) => <div key={i}>{t}</div>)}
        </div>
    );
}

function RefTable({ cols, groups, noteText = {} }) {
    return (
        <div style={{
            background: 'var(--glass-fill)', border: '1px solid var(--glass-border)',
            borderRadius: 12, overflow: 'hidden',
        }}>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%' }}>
                    <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${T.border}` }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, position: 'sticky', left: 0, background: '#1a2030', minWidth: 260 }}>
                                Surface / Dimension
                            </th>
                            {cols.map((c, i) => (
                                <th key={i} style={{ padding: '8px 10px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: T.white, minWidth: 110, borderLeft: `1px solid ${T.border}22` }}>
                                    <div>{c.label}</div>
                                    {c.sub && <div style={{ fontWeight: 400, color: T.muted, marginTop: 2 }}>{c.sub}</div>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((g, gi) => (
                            <React.Fragment key={gi}>
                                {g.title && (
                                    <tr>
                                        <td colSpan={cols.length + 1} style={{
                                            padding: '6px 12px', background: 'rgba(58,138,196,0.08)',
                                            color: T.blue, fontWeight: 700, fontSize: 10, letterSpacing: 0.4,
                                            textTransform: 'uppercase', position: 'sticky', left: 0,
                                        }}>{g.title}</td>
                                    </tr>
                                )}
                                {g.rows.map((r, ri) => (
                                    <tr key={ri} style={{ borderBottom: `1px solid ${T.border}11`, background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                                        <td style={{ padding: '6px 12px', color: T.white, position: 'sticky', left: 0, background: ri % 2 === 0 ? '#1a2030' : '#1c2233' }}>{r.label}</td>
                                        {cols.map((c, ci) => {
                                            const noteLetters = r.notes?.(c);
                                            return (
                                                <td key={ci} style={{ padding: '6px 10px', textAlign: 'center', color: T.muted, borderLeft: `1px solid ${T.border}11`, fontVariantNumeric: 'tabular-nums' }}>
                                                    {r.cell(c)}
                                                    {noteLetters?.length ? (
                                                        <sup
                                                            title={noteLetters.map(n => `${n}. ${noteText[n] || ''}`).join('  ')}
                                                            style={{ color: T.blue, marginLeft: 2, fontWeight: 700, cursor: 'help' }}
                                                        >{noteLetters.join(',')}</sup>
                                                    ) : null}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function OlsParametersTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sub, setSub] = useState('t41');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getOlsParameters();
            setRows(data.rows || []);
        } catch (e) {
            console.error('Failed to load OLS parameters', e);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const map = useMemo(() => buildParamMap(rows), [rows]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 4, background: 'var(--glass-fill)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: 4 }}>
                    {[{ key: 't41', label: 'Table 4-1' }, { key: 't42', label: 'Table 4-2' }, { key: 'aerodrome', label: 'By Aerodrome' }].map(t => (
                        <button key={t.key} onClick={() => setSub(t.key)} style={{
                            padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                            background: sub === t.key ? 'var(--blue-glass-fill)' : 'transparent',
                            color: sub === t.key ? T.white : T.muted,
                            fontSize: 12, fontWeight: sub === t.key ? 600 : 400,
                        }}>{t.label}</button>
                    ))}
                </div>
                <span style={{ fontSize: 11, color: T.muted }}>
                    {sub === 't41'
                        ? 'Approach / Transitional / Inner Horizontal / Conical / OFZ dimensions — Annex 14 Amendment 18'
                        : sub === 't42'
                        ? 'Take-off Climb surface dimensions — runways meant for take-off'
                        : 'Type an ICAO designator to pull its legacy OLS surface table straight from thr_table/arp_table'}
                </span>
                {sub !== 'aerodrome' && (
                    <button onClick={load} className="dg-btn dg-btn-ghost" style={{ marginLeft: 'auto', fontSize: 11, padding: '4px 10px' }}>
                        🔄 Refresh
                    </button>
                )}
            </div>

            {sub === 'aerodrome' ? (
                <OlsByAerodromeTab />
            ) : loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>Loading OLS parameters...</div>
            ) : sub === 't41' ? (
                <T41Table map={map} />
            ) : (
                <T42Table map={map} />
            )}
        </div>
    );
}

// ─── OLS Parameters — By Aerodrome (legacy runway-pair table) ────────────────
// Type an ICAO designator, hit Enter — pairs up thr_table rows two-at-a-time
// per runway and pivots each pair's classification+code through the same
// ols_surfaces.py lookup tables the app's own OLS report uses. Read-only,
// no project required.

function olsPct(s) { return s || '—'; }

function OlsPairCard({ pair }) {
    const rwyLabel = `RWY ${pair.rwy1} / ${pair.rwy2}`;
    return (
        <div style={{ background: 'var(--glass-fill)', border: '1px solid var(--glass-border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{
                padding: '10px 16px', borderBottom: `1px solid ${T.border}`,
                display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                background: 'rgba(58,138,196,0.06)',
            }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.white }}>{rwyLabel}</span>
                <span style={{ fontSize: 11, color: T.muted }}>Classification <b style={{ color: T.white, fontWeight: 600 }}>{pair.classification}</b></span>
                <span style={{ fontSize: 11, color: T.muted }}>Code <b style={{ color: T.white, fontWeight: 600 }}>{pair.code ?? '—'}</b></span>
                <span style={{ fontSize: 11, color: T.muted }}>Threshold-to-threshold <b style={{ color: T.white, fontWeight: 600 }}>{pair.rwy_length_m != null ? `${num(pair.rwy_length_m)} m` : '—'}</b></span>
            </div>

            <div style={{ padding: '10px 16px', fontSize: 11, color: T.muted }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 16px' }}>
                    <div />
                    <div style={{ color: T.white, fontWeight: 600 }}>RWY {pair.rwy1}</div>
                    <div style={{ color: T.white, fontWeight: 600 }}>RWY {pair.rwy2}</div>
                    <div>Latitude</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{pair.thr1_lat?.toFixed(6)}</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{pair.thr2_lat?.toFixed(6)}</div>
                    <div>Longitude</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{pair.thr1_lon?.toFixed(6)}</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{pair.thr2_lon?.toFixed(6)}</div>
                    <div>Elevation</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{pair.thr1_elev_m} m · {pair.thr1_elev_ft} ft</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{pair.thr2_elev_m} m · {pair.thr2_elev_ft} ft</div>
                    <div>Stopway / Clearway</div><div>{num(pair.swy1_m)} / {num(pair.cwy1_m)} m</div><div>{num(pair.swy2_m)} / {num(pair.cwy2_m)} m</div>
                    <div>AD Ref Elev</div><div colSpan={2} style={{ gridColumn: 'span 2', fontVariantNumeric: 'tabular-nums' }}>{pair.arp_elev_m} m · {pair.arp_elev_ft} ft</div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', borderTop: `1px solid ${T.border}` }}>
                {[
                    { title: 'Runway Strip', rows: [
                        ['Length (pre-thr ext.)', `${pair.strip_pre_thr_m} / ${pair.strip_pre_thr_m} m`],
                        [pair.strip_width_is_measured ? 'Width (AIP-measured)' : 'Width (legacy standard)', `${num(pair.strip_half_width_m * 2)} m`],
                    ]},
                    { title: 'Transitional Surface', rows: [['Slope', `${olsPct(pair.trans_slope_pct)} / ${olsPct(pair.trans_slope_pct)}`]] },
                    { title: 'Inner Horizontal Surface', rows: [
                        ['Height', `${pair.ihs_height_m} / ${pair.ihs_height_m} m`],
                        ['Radius', `${num(pair.ihs_radius_m)} / ${num(pair.ihs_radius_m)} m`],
                    ]},
                    { title: 'Conical Surface', rows: [
                        ['Slope', `${pair.conical_slope_pct} / ${pair.conical_slope_pct}`],
                        ['Height', `${pair.conical_height_m} / ${pair.conical_height_m} m`],
                    ]},
                    { title: 'Outer Horizontal Surface', rows: [
                        ['Height', `${pair.ohs_height_m} / ${pair.ohs_height_m} m`],
                        ['Radius', `${num(pair.ohs_radius_m)} / ${num(pair.ohs_radius_m)} m`],
                    ]},
                    { title: 'Approach Surface', rows: [
                        ['Inner edge length', `${num(pair.app_inner_edge_m)} / ${num(pair.app_inner_edge_m)} m`],
                        ['Dist. from threshold', `${pair.app_dist_thr_m} / ${pair.app_dist_thr_m} m`],
                        ['Divergence', `${olsPct(pair.app_divergence_pct)} / ${olsPct(pair.app_divergence_pct)}`],
                        ['Sections', (pair.app_sections || []).map(s => `${num(s.length_m)} m @ ${s.slope_pct}`).join(' → ') || '—'],
                    ]},
                    { title: 'Take-off Climb Surface', rows: [
                        ['Inner edge length', `${pair.tocs_inner_edge_m} / ${pair.tocs_inner_edge_m} m`],
                        ['Dist. from rwy end', `${pair.tocs_dist_rwy_end_m} / ${pair.tocs_dist_rwy_end_m} m`],
                        ['Divergence', `${pair.tocs_divergence_pct} / ${pair.tocs_divergence_pct}`],
                        ['Final width', `${num(pair.tocs_final_width_m)} / ${num(pair.tocs_final_width_m)} m`],
                        ['Length', `${num(pair.tocs_length_m)} / ${num(pair.tocs_length_m)} m`],
                        ['Slope', `${pair.tocs_slope_pct} / ${pair.tocs_slope_pct}`],
                    ]},
                ].map((block, bi) => (
                    <div key={bi} style={{ padding: '10px 16px', borderRight: `1px solid ${T.border}11`, borderBottom: `1px solid ${T.border}11` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.blue, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{block.title}</div>
                        {block.rows.map(([label, val], ri) => (
                            <div key={ri} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, padding: '2px 0', color: T.muted }}>
                                <span>{label}</span>
                                <span style={{ color: T.white, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{val}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

function OlsByAerodromeTab() {
    const [icao, setIcao] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const runLookup = useCallback(async (code) => {
        const clean = (code || '').trim().toUpperCase();
        if (!clean) return;
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await getOlsLegacyByAerodrome(clean);
            setData(res);
        } catch (e) {
            setData(null);
            setError(e?.response?.data?.detail || 'Lookup failed');
        } finally { setLoading(false); }
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                    value={icao}
                    onChange={e => setIcao(e.target.value.toUpperCase())}
                    onKeyDown={e => { if (e.key === 'Enter') runLookup(icao); }}
                    placeholder="ICAO designator, e.g. FAHS"
                    maxLength={4}
                    style={{
                        width: 220, padding: '8px 12px', borderRadius: 8,
                        border: `1px solid ${T.border}`, background: 'var(--glass-fill)',
                        color: T.white, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase',
                    }}
                />
                <button onClick={() => runLookup(icao)} className="dg-btn dg-btn-primary" style={{ fontSize: 12, padding: '8px 16px' }} disabled={loading}>
                    {loading ? 'Looking up…' : 'Get parameters'}
                </button>
            </div>

            {error && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,80,80,0.1)', border: '1px solid rgba(220,80,80,0.3)', color: '#e08080', fontSize: 12 }}>
                    {error}
                </div>
            )}

            {data && (
                <>
                    <div style={{ fontSize: 12, color: T.muted }}>
                        <b style={{ color: T.white }}>{data.icao}</b>{data.airport_name ? ` — ${data.airport_name}` : ''} · AD Ref Elev {data.arp_elev_m} m · {data.arp_elev_ft} ft
                    </div>
                    {data.runway_pairs.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: T.muted, fontSize: 12 }}>No runway pairs found.</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {data.runway_pairs.map((pair, i) => <OlsPairCard key={i} pair={pair} />)}
                        </div>
                    )}
                    <div style={{ fontSize: 10.5, color: T.muted, lineHeight: 1.6 }}>
                        Threshold facts (lat/lon/elevation/stopway/clearway) come straight from <code>thr_table</code> / <code>arp_table</code> — unedited.
                        Surface figures (strip/transitional/inner horizontal/conical/outer horizontal/approach/take-off climb) are the classification+code
                        standard lookups from <code>ols_surfaces.py</code>, the same source the app's own OLS report reads. Read-only — nothing here writes to the database.
                    </div>
                </>
            )}
        </div>
    );
}

// ─── Obstacles Reference Data Tab (SWOT / SOF — shielding principle) ──────────
// SWOT: Terrain/Control Points spot heights & trig beacons (~420k rows,
// static survey reference). SOF: CAA "Schedule of Obstacles"
// (constructed/proposed, ~40k rows), the project owner re-uploads monthly.
// Both fully replace the existing table on upload — the "version date" shown
// is read off the end of the uploaded filename (e.g. SOF_20260806.csv).

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Plots whatever's currently loaded in the panel (the active search
// page — up to page_size rows), not the whole dataset. SWOT alone is ~420k
// rows; rendering all of it as markers would freeze the browser, and a
// reference-lookup tool is naturally used narrowed-down (search first,
// then look at the map), not as a "show me everything" viewer.
const _POPUP_FIELD_LABELS = {
    type: 'Type', caa_id: 'CAA ID', site_name: 'Site Name', status: 'Status',
    elev_m: 'Elev (m)', hgt_m: 'Height (m)',
};

// Two-level map view: first shows every ~100km UTM block as a clickable
// rectangle (a cheap ~150-200 row summary query), then — only once a block
// is clicked — fetches and plots that block's own obstacles (a few hundred
// to a few thousand rows, comfortably renderable as plain markers). Replaces
// an earlier "fetch and cluster the entire dataset" approach that, for
// SWOT's ~420k rows, took 20+ seconds and at one point crashed the whole
// app (an uncaught error deep in the clustering library with no error
// boundary above it unmounted the entire React tree, not just this modal).
function ObstaclesMapModal({ blocksFn, blockFn, label, color, onClose }) {
    const mapRef = useRef(null), mapInst = useRef(null), layerRef = useRef(null);
    const [blocks, setBlocks] = useState(null);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [blockRows, setBlockRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 5 });
        const tl = TILE_LAYERS.street;
        L.tileLayer(tl.url, { attribution: tl.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        return () => { mapInst.current?.remove(); mapInst.current = null; };
    }, []);

    // Block summary — fetched once, on open.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        blocksFn().then(({ data }) => {
            if (cancelled) return;
            setBlocks(data.blocks || []);
        }).catch(e => {
            if (cancelled) return;
            console.error(`Failed to load ${label} blocks`, e);
            setError(e.response?.data?.detail || e.message);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Render block rectangles (while no block is selected).
    useEffect(() => {
        if (!mapInst.current || !blocks || selectedBlock) return;
        if (layerRef.current) { mapInst.current.removeLayer(layerRef.current); layerRef.current = null; }

        const group = L.layerGroup();
        blocks.forEach(b => {
            const bounds = [[b.min_lat, b.min_lon], [b.max_lat, b.max_lon]];
            const rect = L.rectangle(bounds, { color, weight: 1, fillColor: color, fillOpacity: 0.12 });
            rect.on('click', () => setSelectedBlock(b.utm_block));
            group.addLayer(rect);
            const label_ = L.marker([(b.min_lat + b.max_lat) / 2, (b.min_lon + b.max_lon) / 2], {
                icon: L.divIcon({
                    html: `<div style="background:${color}dd;color:#fff;border-radius:12px;padding:2px 8px;font-size:11px;font-weight:700;white-space:nowrap;border:1px solid ${color}">${b.count.toLocaleString()}</div>`,
                    className: '', iconSize: null,
                }),
                interactive: false,
            });
            group.addLayer(label_);
        });
        layerRef.current = group;
        group.addTo(mapInst.current);
        if (blocks.length > 0) {
            const allPts = blocks.flatMap(b => [[b.min_lat, b.min_lon], [b.max_lat, b.max_lon]]);
            mapInst.current.fitBounds(L.latLngBounds(allPts).pad(0.05));
        }
    }, [blocks, selectedBlock, color]);

    // Fetch the selected block's own rows.
    useEffect(() => {
        if (!selectedBlock) { setBlockRows(null); return; }
        let cancelled = false;
        setLoading(true);
        setError('');
        blockFn(selectedBlock).then(({ data }) => {
            if (cancelled) return;
            setBlockRows((data.rows || []).filter(r => r && r.lat != null && r.lon != null));
        }).catch(e => {
            if (cancelled) return;
            console.error(`Failed to load ${label} block ${selectedBlock}`, e);
            setError(e.response?.data?.detail || e.message);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBlock]);

    // Render the selected block's obstacles as plain markers.
    useEffect(() => {
        if (!mapInst.current || !blockRows) return;
        if (layerRef.current) { mapInst.current.removeLayer(layerRef.current); layerRef.current = null; }

        const markers = blockRows.map(r => {
            const m = L.circleMarker([r.lat, r.lon], { radius: 5, color, weight: 1, fillColor: color, fillOpacity: 0.8 });
            const html = Object.entries(r)
                .filter(([k, v]) => !['ogc_fid', 'lat', 'lon'].includes(k) && v != null && v !== '')
                .map(([k, v]) => `<b>${_POPUP_FIELD_LABELS[k] || k}:</b> ${v}`)
                .join('<br/>');
            m.bindPopup(html);
            return m;
        });
        const group = L.layerGroup(markers);
        layerRef.current = group;
        group.addTo(mapInst.current);
        if (blockRows.length > 0) {
            mapInst.current.fitBounds(L.latLngBounds(blockRows.map(r => [r.lat, r.lon])).pad(0.15));
        }
    }, [blockRows, color]);

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 3000, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.bg1, borderBottom: `1px solid ${T.border}`, gap: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 10 }}>
                    {selectedBlock && (
                        <button onClick={() => setSelectedBlock(null)} className="dg-btn dg-btn-ghost" style={{ fontSize: 11 }}>
                            ← Back to blocks
                        </button>
                    )}
                    <span>
                        {label}
                        {!selectedBlock && blocks && ` — ${blocks.length.toLocaleString()} blocks (click one to view its obstacles)`}
                        {selectedBlock && ` — Block ${selectedBlock}`}
                        {selectedBlock && blockRows && ` — ${blockRows.length.toLocaleString()} obstacles`}
                        {loading && ' — loading…'}
                        {error && <span style={{ color: T.danger }}> — {error}</span>}
                    </span>
                </div>
                <button onClick={onClose} className="dg-btn dg-btn-ghost">✕ Close</button>
            </div>
            <div ref={mapRef} style={{ flex: 1 }} />
        </div>
    );
}

function ObstaclesDatasetPanel({ dataset, label, description, meta, onMetaChange, fetchFn, uploadFn, blocksFn, blockFn, columns, searchPlaceholder, mapColor = '#3aa8c4' }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(2000);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadMsg, setUploadMsg] = useState('');
    const [showMap, setShowMap] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(async (searchVal, pageVal) => {
        setLoading(true);
        try {
            const { data } = await fetchFn(searchVal || undefined, pageVal);
            setRows(data.rows || []);
            setTotal(data.total || 0);
            setPageSize(data.page_size || 2000);
        } catch (e) {
            console.error(`Failed to load ${dataset}`, e);
            setRows([]); setTotal(0);
        } finally { setLoading(false); }
    }, [fetchFn, dataset]);

    useEffect(() => { load(search, page); }, [page]); // eslint-disable-line
    useEffect(() => { setPage(1); load(search, 1); }, [search]); // eslint-disable-line

    async function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!window.confirm(`Upload "${file.name}" (${formatBytes(file.size)})? This REPLACES the entire current ${label} dataset.`)) {
            if (fileRef.current) fileRef.current.value = '';
            return;
        }
        setUploading(true);
        setUploadMsg('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await uploadFn(fd);
            if (data.error) {
                setUploadMsg(`✕ ${data.error}`);
            } else {
                setUploadMsg(`✓ Loaded ${data.row_count.toLocaleString()} rows (version ${data.version_date || 'unknown'})`);
                onMetaChange();
                setPage(1);
                load(search, 1);
            }
        } catch (e) {
            setUploadMsg(`✕ ${e.response?.data?.detail || e.message}`);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 11, color: T.muted }}>{description}</div>

            <div style={{
                display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
                background: 'var(--glass-fill)', border: '1px solid var(--glass-border)',
                borderRadius: 10, padding: '10px 14px',
            }}>
                {meta ? (
                    <>
                        <div>
                            <div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase' }}>Current Version</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.white }}>{meta.version_date || 'Unknown'}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase' }}>Rows</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.white }}>{meta.row_count.toLocaleString()}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase' }}>Uploaded</div>
                            <div style={{ fontSize: 12, color: T.muted }}>{meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleString() : '—'}</div>
                        </div>
                    </>
                ) : (
                    <div style={{ fontSize: 12, color: '#f5a623', fontWeight: 600 }}>⚠ No {label} data uploaded yet</div>
                )}
                <label style={{ marginLeft: 'auto' }}>
                    <input ref={fileRef} type="file" accept=".csv" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
                    <span onClick={() => fileRef.current?.click()} style={{
                        display: 'inline-block', padding: '6px 14px', borderRadius: 6, cursor: uploading ? 'not-allowed' : 'pointer',
                        background: T.blue, color: T.white, fontSize: 11, fontWeight: 700, opacity: uploading ? 0.6 : 1,
                    }}>
                        {uploading ? '⏳ Uploading…' : `📤 Upload New ${label} CSV`}
                    </span>
                </label>
            </div>
            {uploadMsg && <div style={{ fontSize: 11, color: uploadMsg.startsWith('✓') ? T.safe : T.danger }}>{uploadMsg}</div>}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                    type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder={searchPlaceholder} className="dg-input"
                    style={{ width: 260, fontSize: 12, padding: '6px 10px' }}
                />
                <span style={{ fontSize: 11, color: T.muted }}>
                    {total.toLocaleString()} match{total !== 1 ? 'es' : ''}
                    {totalPages > 1 && ` · page ${page} of ${totalPages}`}
                </span>
                <Btn small variant="ghost" onClick={() => setShowMap(true)} disabled={!meta}>
                    🗺 View on Map (by Block)
                </Btn>
                {totalPages > 1 && (
                    <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                        <Btn small onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>← Prev</Btn>
                        <Btn small onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next →</Btn>
                    </div>
                )}
            </div>

            <div style={{
                background: 'var(--glass-fill)', border: '1px solid var(--glass-border)',
                borderRadius: 12, overflow: 'hidden',
            }}>
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>
                        {search ? 'No matches.' : (meta ? 'No rows.' : `Upload a ${label} CSV to get started.`)}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', maxHeight: 520 }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%' }}>
                            <thead>
                                <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0 }}>
                                    {columns.map(c => (
                                        <th key={c.key} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, whiteSpace: 'nowrap', background: '#1a2030' }}>{c.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, ri) => (
                                    <tr key={r.ogc_fid} style={{ borderBottom: `1px solid ${T.border}11`, background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                                        {columns.map(c => (
                                            <td key={c.key} style={{ padding: '6px 10px', color: T.muted, whiteSpace: 'nowrap' }}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showMap && (
                <ObstaclesMapModal
                    blocksFn={blocksFn} blockFn={blockFn} label={label}
                    color={mapColor} onClose={() => setShowMap(false)}
                />
            )}
        </div>
    );
}

const SWOT_COLS = [
    { key: 'type', label: 'Type' },
    { key: 'caa_id', label: 'CAA ID' },
    { key: 'lat', label: 'Latitude', render: r => r.lat?.toFixed(6) },
    { key: 'lon', label: 'Longitude', render: r => r.lon?.toFixed(6) },
    { key: 'site_elev_m', label: 'Site Elev (m)', render: r => r.site_elev_m?.toFixed(2) },
    { key: 'elev_m', label: 'Elev (m)', render: r => r.elev_m?.toFixed(2) },
    { key: 'h_tolerance_m', label: 'H-Tol (m)' },
    { key: 'v_tolerance_m', label: 'V-Tol (m)' },
    { key: 'source', label: 'Source' },
    { key: 'source_date', label: 'Source Date' },
];

const SOF_COLS = [
    { key: 'type', label: 'Type' },
    { key: 'caa_id', label: 'CAA ID' },
    { key: 'site_name', label: 'Site Name' },
    { key: 'status', label: 'Status' },
    { key: 'lat', label: 'Latitude', render: r => r.lat?.toFixed(6) },
    { key: 'lon', label: 'Longitude', render: r => r.lon?.toFixed(6) },
    { key: 'site_elev_m', label: 'Site Elev (m)', render: r => r.site_elev_m?.toFixed(2) },
    { key: 'hgt_m', label: 'Height (m)' },
    { key: 'elev_m', label: 'Elev (m)', render: r => r.elev_m?.toFixed(2) },
    { key: 'h_tolerance_m', label: 'H-Tol (m)' },
    { key: 'v_tolerance_m', label: 'V-Tol (m)' },
    { key: 'name', label: 'Region' },
];

function ObstaclesRefTab() {
    const [sub, setSub] = useState('swot');
    const [meta, setMeta] = useState({});

    const loadMeta = useCallback(async () => {
        try {
            const { data } = await getObstaclesMeta();
            setMeta(data || {});
        } catch (e) { console.error('Failed to load obstacles meta', e); }
    }, []);

    useEffect(() => { loadMeta(); }, [loadMeta]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 4, background: 'var(--glass-fill)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: 4, width: 'fit-content' }}>
                {[{ key: 'swot', label: 'SWOT' }, { key: 'sof', label: 'SOF' }].map(t => (
                    <button key={t.key} onClick={() => setSub(t.key)} style={{
                        padding: '5px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: sub === t.key ? 'var(--blue-glass-fill)' : 'transparent',
                        color: sub === t.key ? T.white : T.muted,
                        fontSize: 12, fontWeight: sub === t.key ? 600 : 400,
                    }}>{t.label}</button>
                ))}
            </div>

            {sub === 'swot' ? (
                <ObstaclesDatasetPanel
                    key="swot"
                    dataset="swot" label="SWOT"
                    description="Terrain spot heights and trig beacons — static survey reference data used to shield new application obstacles when no published minima exist nearby."
                    meta={meta.swot} onMetaChange={loadMeta}
                    fetchFn={getObstaclesSwot} uploadFn={uploadObstaclesSwot}
                    blocksFn={getObstaclesSwotBlocks} blockFn={getObstaclesSwotBlock}
                    columns={SWOT_COLS} searchPlaceholder="Search CAA ID..." mapColor="#3aa8c4"
                />
            ) : (
                <ObstaclesDatasetPanel
                    key="sof"
                    dataset="sof" label="SOF"
                    description="CAA Schedule of Obstacles — constructed/proposed obstacles, status-tracked, re-uploaded monthly."
                    meta={meta.sof} onMetaChange={loadMeta}
                    fetchFn={getObstaclesSof} uploadFn={uploadObstaclesSof}
                    blocksFn={getObstaclesSofBlocks} blockFn={getObstaclesSofBlock}
                    columns={SOF_COLS} searchPlaceholder="Search site name or CAA ID..." mapColor="#e8a640"
                />
            )}
        </div>
    );
}

// ─── RMAC Areas Tab ────────────────────────────────────────────────────────────
function RmacManagerTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [reparseLoading, setReparseLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const RMAC_COLS = [
        { key: 'aerodrome',  label: 'Aerodrome',  w: 100 },
        { key: 'chart_name', label: 'Chart Name', w: 180 },
        { key: 'minima_list',label: 'Sector Minima (ft)', w: 260 },
        { key: 'has_layers', label: 'KMZ JSON',   w: 90 },
        { key: 'has_image',  label: 'PNG Image',  w: 90 },
        { key: 'uploaded_at',label: 'Uploaded',   w: 180 },
    ];

    const allSelected = rows.length > 0 && selectedIds.size === rows.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    function toggleAll(checked) { setSelectedIds(checked ? new Set(rows.map(r => r.id)) : new Set()); }
    function toggleRow(id, checked) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [listRes, minRes] = await Promise.all([getRmacList(), getAllRmacMinima()]);
            const list = listRes.data || [];
            const mins = minRes.data || [];
            
            const minMap = {};
            mins.forEach(m => {
                if (!minMap[m.aerodrome]) minMap[m.aerodrome] = [];
                minMap[m.aerodrome].push(m);
            });
            
            list.forEach(r => {
                r.minima_list = minMap[r.aerodrome] || [];
            });
            setRows(list);
        } catch (e) {
            console.error('Failed to load RMAC areas', e);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);
    
    function flash(msg) { setSaved(msg); setTimeout(() => setSaved(''), 2500); }
    function formatDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }

    async function handleDelete(id) {
        if (!window.confirm('Delete this RMAC record?')) return;
        try {
            await deleteRmac(id);
            setRows(prev => prev.filter(r => r.id !== id));
            setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            flash('✓ Deleted');
        } catch (e) { flash('✕ Delete failed'); }
    }

    async function handleDeleteSelected() {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected RMAC records?`)) return;
        try {
            const res = await bulkDeleteRmac(Array.from(selectedIds));
            await load();
            setSelectedIds(new Set());
            flash(`✓ Deleted ${res.data.deleted || selectedIds.size} record(s)`);
        } catch (e) { flash(`✕ Deletion failed`); }
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadRmac();
            setBulkResult(data);
            if (data.uploaded?.length) await load();
        } catch (e) {
            setBulkResult({ uploaded: [], skipped: [], errors: [{ file: 'server', error: e.response?.data?.detail || e.message }] });
        } finally { setBulkLoading(false); }
    }

    async function handleReparse() {
        if (!window.confirm('Re-parse json_layers and images for all RMAC areas that possess a stored KMZ blob?')) return;
        setReparseLoading(true);
        try {
            const { data } = await reparseAllRmac();
            const n = data.updated?.length || 0;
            const e = data.errors?.length || 0;
            flash(`✓ Updated ${n} record(s)${e ? ` (${e} errors)` : ''}`);
            if (n) await load();
        } catch (e) {
            const msg = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message);
            flash('✕ Re-parse failed: ' + msg);
        } finally {
            setReparseLoading(false);
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Btn variant="success" small onClick={() => setShowAdd(true)}>+ Add RMAC Record</Btn>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}>
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                <Btn small onClick={handleReparse} disabled={reparseLoading} title="Re-parse JSON layers and images from stored KMZ blobs">
                    {reparseLoading ? '⏳ Parsing…' : '🔄 Re-parse JSON'}
                </Btn>
                {selectedIds.size > 0 && (
                    <Btn small variant="danger" onClick={handleDeleteSelected}>
                        🗑 Delete Selected ({selectedIds.size})
                    </Btn>
                )}
                {saved && <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>}
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>{rows.length} record(s)</span>
            </div>

            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {loading ? <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                 : rows.length === 0 ? <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>No RMAC records yet.</div>
                 : (
                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 310px)' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.bg0 }}>
                                <tr>
                                    <th style={{ padding: '8px 10px', width: 36, borderBottom: `1px solid ${T.border}` }}>
                                        <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={e => toggleAll(e.target.checked)} style={{ cursor: 'pointer' }} />
                                    </th>
                                    {RMAC_COLS.map(c => <th key={c.key} style={{ padding: '8px 12px', textAlign: 'left', width: c.w, color: T.white, borderBottom: `1px solid ${T.border}` }}>{c.label}</th>)}
                                    <th style={{ padding: '8px 12px', width: 70, borderBottom: `1px solid ${T.border}` }} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={row.id} style={{ background: selectedIds.has(row.id) ? 'rgba(232,64,64,0.06)' : ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)', borderBottom: `1px solid ${T.border}22` }}>
                                        <td style={{ padding: '6px 10px', width: 36 }}><input type="checkbox" checked={selectedIds.has(row.id)} onChange={e => toggleRow(row.id, e.target.checked)} style={{ cursor: 'pointer' }} /></td>
                                        {RMAC_COLS.map(c => (
                                            <td key={c.key} style={{ padding: '8px 12px', color: T.white, width: c.w }}>
                                                {c.key === 'uploaded_at' ? formatDate(row[c.key])
                                                : c.key === 'minima_list' ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: '100%', overflow: 'hidden' }}>
                                                        {row[c.key].length === 0 ? <span style={{ color: T.muted }}>No minimas</span> : row[c.key].map((m, idx) => (
                                                            <span key={idx} style={{ background: 'rgba(155,89,182,0.15)', color: '#d580ff', padding: '2px 6px', borderRadius: 4, fontSize: 10, whiteSpace: 'nowrap' }}>
                                                                {m.sector_name}: <b>{m.minima_ft}</b>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )
                                                : (c.key === 'has_layers' || c.key === 'has_image') ? (row[c.key] ? <span style={{ color: T.safe }}>✓</span> : <span style={{ color: '#e84040' }}>✗</span>)
                                                : row[c.key] || '—'}
                                            </td>
                                        ))}
                                        <td style={{ padding: '6px 8px', textAlign: 'center', width: 70 }}>
                                            <button onClick={() => handleDelete(row.id)} style={{ background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030', color: '#e84040', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>✕ Del</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {showAdd && <AddRmacModal onClose={() => setShowAdd(false)} onAdded={row => { setRows(p => [row, ...p]); flash('✓ Uploaded'); }} />}
            {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
        </div>
    );
}

// ─── RNAV Routes Tab ─────────────────────────────────────────────────────────
function RnavRoutesTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [reparseLoading, setReparseLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getRnavRoutes();
            setRows(data || []);
        } catch (e) {
            console.error('Failed to load RNAV routes', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleDelete(id) {
        if (!window.confirm('Delete this RNAV Route?')) return;
        try {
            await deleteRnavRoute(id);
            setRows(prev => prev.filter(r => r.id !== id));
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleDeleteAll() {
        if (rows.length === 0) return;
        if (!window.confirm(`Delete ALL ${rows.length} RNAV routes? This cannot be undone.`)) return;
        try {
            await clearAllRnavRoutes();
            await load();
            flash('✓ All RNAV routes deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadRnavRoutes();
            flash(`✓ ${data.message || 'Bulk upload completed'}`);
            await load();
        } catch (e) {
            flash('✕ Bulk upload failed');
        } finally {
            setBulkLoading(false);
        }
    }

    async function handleReparse() {
        if (!window.confirm('Re-parse all RNAV routes from their stored KML blobs?')) return;
        setReparseLoading(true);
        try {
            const { data } = await reparseRnavRoutes();
            flash(`✓ ${data.message || 'Reparse completed'}`);
            await load();
        } catch (e) {
            flash('✕ Reparse failed');
        } finally {
            setReparseLoading(false);
        }
    }

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    const filteredRows = rows.filter(r => 
        (r.route_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search by route name..."
                    className="dg-input"
                    style={{ width: 220, fontSize: 12, padding: '6px 10px' }}
                />
                <Btn variant="success" small onClick={() => setShowAdd(true)}>+ Add Route</Btn>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}>
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                <Btn small onClick={handleReparse} disabled={reparseLoading}
                    title="Re-parse route coordinates from stored KML blobs">
                    {reparseLoading ? '⏳ Parsing…' : '🔄 Re-parse JSON'}
                </Btn>
                <Btn small onClick={handleDeleteAll} disabled={rows.length === 0}
                    style={{ background: 'rgba(232,64,64,0.08)', border: '1px solid #e8404040', color: '#e84040', marginLeft: 'auto' }}
                    title="Delete all routes">
                    🗑 Delete All
                </Btn>
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted }}>
                    {filteredRows.length} route{filteredRows.length !== 1 ? 's' : ''}
                </span>
            </div>

            <div style={{
                background: 'var(--glass-fill)', border: '1px solid var(--glass-border)',
                borderRadius: 12, overflow: 'hidden',
            }}>
                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>Loading RNAV routes...</div>
                ) : filteredRows.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>No RNAV routes found.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                            <thead>
                                <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${T.border}` }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, width: 80 }}>ID</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, width: 250 }}>Route Name (Tag &lt;name&gt;)</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: T.white, width: 150 }}>Waypoints Count</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, width: 200 }}>Uploaded At</th>
                                    <th style={{ padding: '8px 12px', width: 100 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((row, ri) => (
                                    <tr key={row.id} style={{
                                        borderBottom: `1px solid ${T.border}22`,
                                        background: ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)',
                                    }}>
                                        <td style={{ padding: '8px 12px', color: T.white }}>{row.id}</td>
                                        <td style={{ padding: '8px 12px', color: T.white, fontWeight: 600 }}>{row.route_name}</td>
                                        <td style={{ padding: '8px 12px', color: T.white, textAlign: 'center' }}>{row.point_count}</td>
                                        <td style={{ padding: '8px 12px', color: T.muted }}>
                                            {row.uploaded_at ? new Date(row.uploaded_at).toLocaleString() : '—'}
                                        </td>
                                        <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleDelete(row.id)}
                                                style={{
                                                    background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030',
                                                    color: '#e84040', borderRadius: 4, padding: '2px 8px',
                                                    fontSize: 11, cursor: 'pointer',
                                                }}
                                            >✕ Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showAdd && (
                <AddRnavRouteModal
                    onClose={() => setShowAdd(false)}
                    onAdded={() => {
                        load();
                        flash('✓ File uploaded and parsed successfully');
                    }}
                />
            )}
        </div>
    );
}

// ─── Add RNAV Route Modal ─────────────────────────────────────────────────────
function AddRnavRouteModal({ onClose, onAdded }) {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    async function submit() {
        if (!file) { setErr('Please select a .kml or .kmz file'); return; }
        setLoading(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await uploadRnavRoute(fd);
            if (data.error) { setErr(data.error); return; }
            onAdded();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600,
        }}>
            <div style={{
                background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12,
                padding: 28, width: 420,
            }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>🛣️ Upload RNAV Route File</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>KMZ or KML File *</div>
                        <input
                            type="file"
                            accept=".kmz,.kml"
                            onChange={e => setFile(e.target.files[0])}
                            style={{
                                background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
                                padding: '10px', color: T.white, fontSize: 12, width: '100%', boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    {err && <div style={{ color: '#e84040', fontSize: 12 }}>{err}</div>}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'transparent', border: `1px solid ${T.border}`,
                                color: T.muted, borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 12
                            }}
                        >Cancel</button>
                        <button
                            onClick={submit}
                            disabled={loading || !file}
                            style={{
                                background: T.blue, border: 'none', color: T.white,
                                borderRadius: 6, padding: '7px 20px', cursor: 'pointer',
                                fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6
                            }}
                        >
                            {loading ? '⏳ Uploading...' : '📤 Upload'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Airspace Manager Tab ─────────────────────────────────────────────────────
function AirspaceTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [reparseLoading, setReparseLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const AIRSPACE_COLS_VIEW = [
        { key: 'uuid', label: 'UUID', w: 200 },
        { key: 'designator', label: 'Designator', w: 120 },
        { key: 'name', label: 'Name', w: 240 },
        { key: 'type', label: 'Type', w: 100 },
        { key: 'interpretation', label: 'Interpretation', w: 120 },
        { key: 'upper_limit', label: 'Upper Limit', w: 100 },
        { key: 'lower_limit', label: 'Lower Limit', w: 100 },
        { key: 'frequency', label: 'Frequency', w: 220 },
    ];

    const allSelected = rows.length > 0 && selectedIds.size === rows.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    function toggleAll(checked) {
        setSelectedIds(checked ? new Set(rows.map(r => r.uuid)) : new Set());
    }
    function toggleRow(id, checked) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getAirspaceData(undefined, 1);
            setRows(data.rows || []);
        } catch (e) {
            console.error('Failed to load airspace', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleDelete(uuid) {
        if (!window.confirm('Delete this airspace?')) return;
        try {
            await deleteAirspace(uuid);
            setRows(prev => prev.filter(r => r.uuid !== uuid));
            setSelectedIds(prev => { const n = new Set(prev); n.delete(uuid); return n; });
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleDeleteSelected() {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} selected airspace(s)?`)) return;
        let failed = 0;
        for (const id of selectedIds) {
            try { await deleteAirspace(id); }
            catch { failed++; }
        }
        await load();
        setSelectedIds(new Set());
        flash(failed ? `✕ ${failed} deletion(s) failed` : `✓ Deleted ${selectedIds.size} airspace(s)`);
    }

    async function handleBulkUpload() {
        setBulkLoading(true);
        try {
            const { data } = await bulkUploadAirspace();
            setBulkResult(data);
            if (data.uploaded?.length) await load();
        } catch (e) {
            setBulkResult({ uploaded: [], skipped: [], errors: [{ file: 'server', error: e.response?.data?.detail || e.message }] });
        } finally {
            setBulkLoading(false);
        }
    }

    async function handleReparse() {
        if (!window.confirm('Re-parse geometry for all airspaces from KMZ files?')) return;
        setReparseLoading(true);
        try {
            const { data } = await reparseAirspace();
            const n = data.updated?.length || 0;
            const e = data.errors?.length || 0;
            flash(`✓ Updated ${n} airspace(s)${e ? ` (${e} errors)` : ''}`);
            if (n) await load();
        } catch (e) {
            const msg = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response?.data?.detail || e.message);
            flash('✕ Re-parse failed: ' + msg);
        } finally {
            setReparseLoading(false);
        }
    }

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Btn variant="success" small onClick={() => setShowAdd(true)}>+ Add Airspace</Btn>
                <Btn small onClick={handleBulkUpload} disabled={bulkLoading}>
                    {bulkLoading ? '⏳ Running…' : '📦 Run Bulk Upload'}
                </Btn>
                <Btn small onClick={handleReparse} disabled={reparseLoading}
                    title="Re-parse geometry from stored KMZ files">
                    {reparseLoading ? '⏳ Parsing…' : '🔄 Re-parse Geometry'}
                </Btn>
                {selectedIds.size > 0 && (
                    <Btn small variant="danger" onClick={handleDeleteSelected}
                        style={{ background: 'rgba(232,64,64,0.15)', border: '1px solid #e8404060', color: '#e84040' }}>
                        🗑 Delete Selected ({selectedIds.size})
                    </Btn>
                )}
                {saved && (
                    <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>
                )}
                <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>
                    {rows.length} airspace(s)
                </span>
            </div>

            {/* Table */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>
                        No airspaces yet.{' '}
                        <span style={{ color: T.blue, cursor: 'pointer' }} onClick={handleBulkUpload}>Run Bulk Upload →</span>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 310px)', scrollbarWidth: 'thin', scrollbarColor: `${T.border} ${T.bg0}` }}>
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', tableLayout: 'fixed' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.bg0 }}>
                                <tr>
                                    <th style={{ padding: '8px 10px', width: 36, borderBottom: `1px solid ${T.border}` }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={el => { if (el) el.indeterminate = someSelected; }}
                                            onChange={e => toggleAll(e.target.checked)}
                                            style={{ accentColor: T.blue, cursor: 'pointer' }}
                                        />
                                    </th>
                                    {AIRSPACE_COLS_VIEW.map(c => (
                                        <th key={c.key} style={{
                                            padding: '8px 12px', textAlign: 'left',
                                            fontSize: 10, fontWeight: 700, color: T.white,
                                            width: c.w, minWidth: c.w,
                                            borderBottom: `1px solid ${T.border}`,
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                        }}>{c.label}</th>
                                    ))}
                                    <th style={{ padding: '8px 12px', width: 70, borderBottom: `1px solid ${T.border}` }} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => (
                                    <tr key={row.uuid} style={{
                                        borderBottom: `1px solid ${T.border}22`,
                                        background: selectedIds.has(row.uuid)
                                            ? 'rgba(232,64,64,0.06)'
                                            : ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)',
                                    }}>
                                        <td style={{ padding: '6px 10px', width: 36 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.uuid)}
                                                onChange={e => toggleRow(row.uuid, e.target.checked)}
                                                style={{ accentColor: T.blue, cursor: 'pointer' }}
                                            />
                                        </td>
                                        {AIRSPACE_COLS_VIEW.map(c => (
                                            <td key={c.key} style={{
                                                padding: '8px 12px', color: T.white,
                                                width: c.w, minWidth: c.w, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {row[c.key] ?? <span style={{ color: T.muted }}>—</span>}
                                            </td>
                                        ))}
                                        <td style={{ padding: '6px 8px', textAlign: 'center', width: 70 }}>
                                            <button
                                                onClick={() => handleDelete(row.uuid)}
                                                style={{
                                                    background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030',
                                                    color: '#e84040', borderRadius: 4, padding: '2px 8px',
                                                    fontSize: 11, cursor: 'pointer',
                                                }}
                                            >✕ Del</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add modal */}
            {showAdd && <AddModal tab="airspace" onClose={() => setShowAdd(false)} onAdded={row => { setRows(p => [row, ...p]); flash('✓ Added'); }} />}
            {bulkResult && <BulkResultModal result={bulkResult} onClose={() => setBulkResult(null)} />}
        </div>
    );
}

// ─── Clients / Coordinators Tab ────────────────────────────────────────────
// Preparation for a future merge with the external "toat" dashboard system
// (which already tracks clients + their coordinator). Project creation reads
// this same client list instead of free-typed company names.
function ClientsTab() {
    const [clients, setClients] = useState([]);
    const [coordinators, setCoordinators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [saved, setSaved] = useState('');
    const [showAddClient, setShowAddClient] = useState(false);
    const [showAddCoord, setShowAddCoord] = useState(false);
    const [editingClient, setEditingClient] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, co] = await Promise.all([getClients(), getCoordinators()]);
            setClients(c.data.rows || []);
            setCoordinators(co.data.rows || []);
        } catch (e) {
            console.error('Failed to load clients/coordinators', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    function flash(msg) {
        setSaved(msg);
        setTimeout(() => setSaved(''), 2500);
    }

    const filtered = useMemo(() => {
        if (!search.trim()) return clients;
        const s = search.trim().toLowerCase();
        return clients.filter(c => c.name.toLowerCase().includes(s) || (c.short_name || '').toLowerCase().includes(s));
    }, [clients, search]);

    async function handleCoordinatorChange(clientId, coordinatorId) {
        try {
            await updateClient(clientId, { coordinator_id: coordinatorId || null });
            setClients(prev => prev.map(c => c.id === clientId
                ? { ...c, coordinator_id: coordinatorId || null, coordinator_name: coordinators.find(co => co.id === coordinatorId)?.name || null }
                : c));
            flash('✓ Saved');
        } catch (e) {
            flash('✕ Update failed');
        }
    }

    async function handleDeleteClient(client) {
        if (!window.confirm(`Delete client "${client.name}"?`)) return;
        try {
            const { data } = await deleteClient(client.id);
            if (data.error) { flash(`✕ ${data.error}`); return; }
            setClients(prev => prev.filter(c => c.id !== client.id));
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    async function handleDeleteCoordinator(coord) {
        if (!window.confirm(`Delete coordinator "${coord.name}"?`)) return;
        try {
            const { data } = await deleteCoordinator(coord.id);
            if (data.error) { flash(`✕ ${data.error}`); return; }
            setCoordinators(prev => prev.filter(c => c.id !== coord.id));
            flash('✓ Deleted');
        } catch (e) {
            flash('✕ Delete failed');
        }
    }

    const selectStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '5px 8px', color: T.white, fontSize: 12, outline: 'none',
        minWidth: 160,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Coordinators */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: T.white }}>Coordinators</div>
                    <Btn small onClick={() => setShowAddCoord(true)}>+ Add Coordinator</Btn>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {coordinators.map(co => (
                        <div key={co.id} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 8,
                            padding: '6px 10px', fontSize: 12,
                        }}>
                            <span style={{ color: T.white, fontWeight: 600 }}>{co.name}</span>
                            {co.email && <span style={{ color: T.muted }}>{co.email}</span>}
                            <button onClick={() => handleDeleteCoordinator(co)} style={{
                                background: 'transparent', border: 'none', color: '#e84040',
                                cursor: 'pointer', fontSize: 12, padding: 0,
                            }}>✕</button>
                        </div>
                    ))}
                    {coordinators.length === 0 && !loading && (
                        <div style={{ color: T.muted, fontSize: 12 }}>No coordinators yet.</div>
                    )}
                </div>
            </div>

            {/* Clients */}
            <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name…"
                        style={{ ...selectStyle, minWidth: 220 }}
                    />
                    <Btn small onClick={() => setShowAddClient(true)}>+ Add Client</Btn>
                    {saved && <span style={{ fontSize: 11, color: saved.startsWith('✓') ? T.safe : '#e84040' }}>{saved}</span>}
                    <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>
                        {filtered.length} of {clients.length} client{clients.length !== 1 ? 's' : ''}
                    </span>
                </div>

                <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                    {loading ? (
                        <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding: 32, textAlign: 'center', color: T.muted }}>No clients found.</div>
                    ) : (
                        <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                            <thead>
                                <tr>
                                    {['Name', 'Short Name', 'Coordinator', ''].map(h => (
                                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.white, borderBottom: `1px solid ${T.border}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c, ri) => (
                                    <tr key={c.id} style={{ borderBottom: `1px solid ${T.border}22`, background: ri % 2 === 0 ? 'transparent' : 'rgba(58,138,196,0.03)' }}>
                                        <td style={{ padding: '8px 12px', color: T.white, cursor: 'pointer' }} onClick={() => setEditingClient(c)}>{c.name}</td>
                                        <td style={{ padding: '8px 12px', color: T.white }}>{c.short_name || <span style={{ color: T.muted }}>—</span>}</td>
                                        <td style={{ padding: '6px 12px' }}>
                                            <select
                                                value={c.coordinator_id || ''}
                                                onChange={e => handleCoordinatorChange(c.id, e.target.value)}
                                                style={selectStyle}
                                            >
                                                <option value="">— none —</option>
                                                {coordinators.map(co => (
                                                    <option key={co.id} value={co.id}>{co.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                            <button onClick={() => handleDeleteClient(c)} style={{ background: 'rgba(232,64,64,0.10)', border: '1px solid #e8404030', color: '#e84040', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>✕ Del</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showAddClient && (
                <AddClientModal
                    coordinators={coordinators}
                    onClose={() => setShowAddClient(false)}
                    onAdded={row => { setClients(prev => [...prev, row].sort((a, b) => a.name.localeCompare(b.name))); flash('✓ Added'); }}
                />
            )}
            {editingClient && (
                <AddClientModal
                    coordinators={coordinators}
                    editing={editingClient}
                    onClose={() => setEditingClient(null)}
                    onAdded={row => {
                        setClients(prev => prev.map(c => c.id === row.id ? { ...c, ...row, coordinator_name: coordinators.find(co => co.id === row.coordinator_id)?.name || null } : c));
                        flash('✓ Saved');
                    }}
                />
            )}
            {showAddCoord && (
                <AddCoordinatorModal
                    onClose={() => setShowAddCoord(false)}
                    onAdded={row => { setCoordinators(prev => [...prev, row].sort((a, b) => a.name.localeCompare(b.name))); flash('✓ Added'); }}
                />
            )}
        </div>
    );
}

function AddClientModal({ coordinators, editing, onClose, onAdded }) {
    const [form, setForm] = useState({
        name: editing?.name || '', short_name: editing?.short_name || '', coordinator_id: editing?.coordinator_id || '',
    });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    async function submit() {
        if (!form.name.trim()) { setErr('Name is required'); return; }
        setLoading(true); setErr('');
        try {
            const payload = { name: form.name.trim(), short_name: form.short_name.trim() || null, coordinator_id: form.coordinator_id || null };
            const { data } = editing ? await updateClient(editing.id, payload) : await createClient(payload);
            if (data.error) { setErr(data.error); return; }
            onAdded(editing ? { id: editing.id, ...payload } : data);
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600 }}>
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 28, width: 420 }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>{editing ? '🏢 Edit Client' : '🏢 Add Client'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Name *</div>
                        <input value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder="Company name" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Short Name</div>
                        <input value={form.short_name} onChange={e => set('short_name', e.target.value)} style={inputStyle} placeholder="e.g. ZUTARI" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Coordinator</div>
                        <select value={form.coordinator_id} onChange={e => set('coordinator_id', e.target.value)} style={inputStyle}>
                            <option value="">— none —</option>
                            {coordinators.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
                        </select>
                    </div>
                </div>
                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>{loading ? '⏳ Saving…' : (editing ? 'Save' : 'Add')}</Btn>
                </div>
            </div>
        </div>
    );
}

function AddCoordinatorModal({ onClose, onAdded }) {
    const [form, setForm] = useState({ name: '', email: '', phone: '' });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const inputStyle = {
        background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 6,
        padding: '7px 10px', color: T.white, fontSize: 12, outline: 'none',
        width: '100%', boxSizing: 'border-box',
    };

    async function submit() {
        if (!form.name.trim()) { setErr('Name is required'); return; }
        setLoading(true); setErr('');
        try {
            const { data } = await createCoordinator({ name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null });
            if (data.error) { setErr(data.error); return; }
            onAdded(data);
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600 }}>
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 28, width: 420 }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 20 }}>👤 Add Coordinator</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Name *</div>
                        <input value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} placeholder="Full name" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Email</div>
                        <input value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} placeholder="name@example.com" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Phone</div>
                        <input value={form.phone} onChange={e => set('phone', e.target.value)} style={inputStyle} placeholder="Optional" />
                    </div>
                </div>
                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 10 }}>{err}</div>}
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>{loading ? '⏳ Saving…' : 'Add'}</Btn>
                </div>
            </div>
        </div>
    );
}

export default function DataManager() {
    const [tab, setTab] = useState('arp');
    const [showMap, setShowMap] = useState(false);

    return (
        <div style={{ padding: 32, overflowY: 'auto', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>Data Manager</div>
                    <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', marginTop: 4, fontWeight: 300 }}>
                        Reference data from PostgreSQL · Click any ARP / CNS cell to edit
                    </div>
                </div>
                <button className="dg-btn dg-btn-ghost" onClick={() => setShowMap(true)}>
                    🗺 View on Map
                </button>
            </div>

            {/* Tab bar */}
            <div style={{
                display: 'flex', gap: 4, flexWrap: 'wrap',
                background: 'var(--glass-fill)',
                backdropFilter: 'blur(var(--blur-md))',
                WebkitBackdropFilter: 'blur(var(--blur-md))',
                border: '1px solid var(--glass-border)',
                borderRadius: 12, padding: 5, width: 'fit-content',
            }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        padding: '6px 14px', borderRadius: 8,
                        border: tab === t.key ? '1px solid var(--blue-glass-border)' : '1px solid transparent',
                        cursor: 'pointer',
                        background: tab === t.key ? 'var(--blue-glass-fill)' : 'transparent',
                        color: tab === t.key ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontSize: 12, fontWeight: tab === t.key ? 600 : 400, transition: 'all 0.15s',
                        fontFamily: 'inherit',
                    }}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'geo'  && <GeoBordersView />}
            {tab === 'proc' && <ProceduresTab />}
            {tab === 'rmac' && <RmacManagerTab />}
            {tab === 'airspace' && <AirspaceTab />}
            {tab === 'rnav' && <RnavRoutesTab />}
            {tab === 'sid'  && <DeparturesTab />}
            {tab === 'star' && <ArrivalsTab />}
            {tab === 'ols_params' && <OlsParametersTab />}
            {tab === 'obstacles_ref' && <ObstaclesRefTab />}
            {tab === 'msa' && <MsaTab />}
            {tab === 'clients' && <ClientsTab />}
            {tab !== 'geo' && tab !== 'proc' && tab !== 'rmac' && tab !== 'airspace' && tab !== 'rnav' && tab !== 'sid' && tab !== 'star' && tab !== 'ols_params' && tab !== 'obstacles_ref' && tab !== 'msa' && tab !== 'clients' && <TableView key={tab} tab={tab} />}

            {/* Map Overlay Modal */}
            {showMap && <DataMapModal onClose={() => setShowMap(false)} />}
        </div>
    );
}

